import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { SHOW_RESOURCES, RESOURCE_CATEGORIES, RESOURCE_TYPES } from "./utils/constants";

// ─── Career progression thresholds ───────────────────────────────────────────
// Hours-based levels (used for progress bar calculation)
// DDESB TP-18 standards at 1,880 hrs/year
const LEVELS = [
  { label: "Sweeper",               min: 0,     max: null,   hoursBased: false, note: "Entry level \u2014 no UXO cert required" },
  { label: "UXO Technician I",      min: 0,     max: 5639,   hoursBased: true },
  { label: "UXO Technician II",     min: 5640,  max: 15039,  hoursBased: true },
  { label: "UXO Technician III",    min: 15040, max: null,   hoursBased: true },
  { label: "UXO Quality Control",   min: 15040, max: null,   hoursBased: false, supervisoryMin: 3760, note: "15,040+ MEC hrs + 3,760 supervisory hrs + quality credential" },
  { label: "UXO Safety Officer / UXOSO", min: 15040, max: null, hoursBased: false, supervisoryMin: 3760, note: "15,040+ MEC hrs + 3,760 supervisory hrs + OSHA 10" },
  { label: "SUXOS - Senior UXO Supervisor", min: 18800, max: null, hoursBased: false, supervisoryMin: 9400, note: "18,800+ MEC hrs + 9,400 supervisory hrs + OSHA 10 + quality credential" },
];

// Only hours-based levels for progress calculation
const HOURS_LEVELS = LEVELS.filter(l => l.hoursBased);

function getLevel(hours) {
  for (let i = HOURS_LEVELS.length - 1; i >= 0; i--) {
    if (hours >= HOURS_LEVELS[i].min) return { ...HOURS_LEVELS[i], index: i };
  }
  return { ...HOURS_LEVELS[0], index: 0 };
}

// ─── Cert status helpers ──────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

function certStatus(expiryDateStr) {
  const days = daysUntil(expiryDateStr);
  if (days === null) return { status: "none", days: null };
  if (days < 0)   return { status: "expired",  days };
  if (days <= 30) return { status: "critical",  days };
  if (days <= 60) return { status: "warning",   days };
  return { status: "active", days };
}

const STATUS_COLOR = {
  lifetime: "#4ade80",
  active:   "#4ade80",
  warning:  "#fbbf24",
  critical: "#f87171",
  expired:  "#f87171",
  missing:  "#555",
  none:     "#555",
};

const STATUS_BG = {
  lifetime: "#0a2a1a",
  active:   "#0a2a1a",
  warning:  "#2a1e08",
  critical: "#2a0a0a",
  expired:  "#2a0a0a",
  missing:  "#1a1c1e",
  none:     "#1a1c1e",
};

function StatusBadge({ status, days, amber }) {
  const color = amber ? "#d97706" : (STATUS_COLOR[status] || "#555");
  const bg    = amber ? "#2a1e08" : (STATUS_BG[status]    || "#1a1c1e");
  let label;
  if (status === "lifetime") label = "Lifetime";
  else if (status === "active")   label = days != null ? `${days}d` : "Active";
  else if (status === "warning")  label = `${days}d`;
  else if (status === "critical") label = `${days}d`;
  else if (status === "expired")  label = "Expired";
  else label = "Not Set";
  return (
    <span style={{ background: bg, color, fontSize: 11, padding: "3px 9px",
                   borderRadius: 12, fontWeight: "bold", whiteSpace: "nowrap",
                   border: `1px solid ${color}33` }}>
      {"\u25CF"} {label}
    </span>
  );
}

// ─── Job match scoring ────────────────────────────────────────────────────────
function techCertSet(profile) {
  const certs = new Set();
  (profile.dod_certs || []).forEach(c => certs.add(c));
  if (profile.hazwoper_40)     certs.add("HAZWOPER 40-Hour");
  if (profile.hazwoper_8)      certs.add("HAZWOPER 8-Hour Refresher");
  if (profile.hazwoper_8_supervisor) certs.add("HAZWOPER 8-Hour Supervisor");
  if (profile.physical_current) certs.add("Current Physical");
  if (profile.clearance)       certs.add("Security Clearance");
  if (profile.dive_cert)       certs.add("Dive Certified");
  if (profile.drivers_license) certs.add("Driver's License");
  if (profile.cdl)             certs.add("CDL");
  if (profile.military_eod)    certs.add("EOD Background");
  if (profile.first_aid_cpr)   certs.add("First Aid/CPR");
  if (profile.osha_10_construction) certs.add("10-Hour OSHA Construction Safety");
  if (profile.quality_credential && profile.quality_credential !== 'None') certs.add("Quality Credential");
  return certs;
}

function scoreJob(job, profile) {
  const techCerts  = techCertSet(profile);
  const required   = job.required_certs  || [];
  const preferred  = job.preferred_certs || [];

  const metRequired  = required.filter(c => techCerts.has(c));
  const metPreferred = preferred.filter(c => techCerts.has(c));
  const missedReq    = required.filter(c => !techCerts.has(c));

  const qualified = missedReq.length === 0;

  const reqScore  = required.length  > 0 ? (metRequired.length  / required.length)  * 70 : 70;
  const prefScore = preferred.length > 0 ? (metPreferred.length / preferred.length) * 30 : 30;
  const matchPct  = Math.round(reqScore + prefScore);

  return { qualified, matchPct, metRequired, metPreferred, missedReq };
}

// ─── Active notifications (read-only) ────────────────────────────────────────
// The `notifications` table is written server-side only. Rows come from the
// service-role edge functions — send-logbook-notification writes the
// logbook_verified / logbook_rejected / quality_cert_verified /
// quality_cert_rejected rows this reads back.
//
// This used to also write its own cert-expiry rows (four cert types x 60/30-day
// alerts, a check-then-insert pair each) on every dashboard load. Those inserts
// were failing RLS and are gone. Deliberately not restored:
//
//   - They were duplicates. `certAlerts` below computes the same four
//     expiries client-side from the same profile fields, and renders them in
//     the same two places.
//   - The only thing lost is a *dismissable* cert alert. Dismissal was already
//     half-broken: notifCount sums server rows + certAlerts, so a working
//     insert showed every expiring cert twice and counted it twice, and
//     dismissing the server row left the client-computed twin on screen.
//   - Expiry emails are unaffected — send-cert-expiry-email handles those
//     server-side, with its own once-per-day throttle.
//
// So restoring dismissable cert alerts is not just an RLS fix: it needs the
// double-count resolved first (have certAlerts render only what has no server
// row, or drop certAlerts and let the server rows be the single source).
async function loadActiveNotifications(profile) {
  if (!profile?.id) return [];

  const today = new Date().toISOString().split("T")[0];
  const { data: active } = await supabase
    .from("notifications")
    .select("*")
    .eq("tech_profile_id", profile.id)
    .lte("scheduled_for", today)
    .is("dismissed_at", null);

  return active || [];
}

// ─── New profile options check ───────────────────────────────────────────────
// Newer columns that may be null if the tech hasn't revisited their profile
const NEW_OPTION_FIELDS = [
  'hazwoper_8_supervisor',
  'first_aid_cpr',
  'state_license',
  'phone',
  'has_logbook',
];

function hasNewOptions(profile) {
  if (!profile) return false;
  return NEW_OPTION_FIELDS.some(field => profile[field] === null || profile[field] === undefined);
}

function isNewOptionsBannerDismissed() {
  const dismissed = localStorage.getItem('uxo_new_options_dismissed');
  if (!dismissed) return false;
  const dismissedAt = parseInt(dismissed, 10);
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedAt < thirtyDays;
}

function dismissNewOptionsBanner() {
  localStorage.setItem('uxo_new_options_dismissed', Date.now().toString());
}

// ─── Lightweight article renderer ────────────────────────────────────────────
// Supports basic formatting: # / ## headings, - bullets, blank-line spacing,
// and **bold** inline. Falls back to plain paragraphs for everything else.
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function ArticleBody({ text }) {
  if (!text) return <div style={{ color: '#7a7570', fontStyle: 'italic' }}>No content.</div>;
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let bullets = [];
  const flushBullets = (key) => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${key}`} style={{ margin: '6px 0 10px', paddingLeft: 22, lineHeight: 1.7 }}>
          {bullets.map((b, i) => <li key={i}>{renderInline(b)}</li>)}
        </ul>
      );
      bullets = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith('## ')) {
      flushBullets(idx);
      blocks.push(<h4 key={idx} style={{ fontSize: 16, fontWeight: 'bold', margin: '14px 0 4px', color: '#e8e4dc' }}>{renderInline(line.slice(3))}</h4>);
    } else if (line.startsWith('# ')) {
      flushBullets(idx);
      blocks.push(<h3 key={idx} style={{ fontSize: 19, fontWeight: 'bold', margin: '16px 0 6px', color: '#e8e4dc' }}>{renderInline(line.slice(2))}</h3>);
    } else if (line.startsWith('- ')) {
      bullets.push(line.slice(2));
    } else if (line.trim() === '') {
      flushBullets(idx);
    } else {
      flushBullets(idx);
      blocks.push(<p key={idx} style={{ margin: '0 0 10px', lineHeight: 1.7 }}>{renderInline(line)}</p>);
    }
  });
  flushBullets('end');
  return <>{blocks}</>;
}

// ─── Main dashboard component ─────────────────────────────────────────────────
export default function TechDashboard({ user, accountType }) {
  const navigate = useNavigate();
  const [profile, setProfile]       = useState(null);
  const [jobs, setJobs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeSection, setActiveSection] = useState(
    () => (SHOW_RESOURCES && typeof window !== "undefined" && window.location.hash === "#resources")
      ? "resources" : "jobs"
  );
  const [notifications, setNotifications] = useState([]);
  // Resource Directory
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourceSearch, setResourceSearch] = useState("");
  const [openArticle, setOpenArticle] = useState(null); // resource id whose article is expanded
  const [dismissing, setDismissing] = useState(null);
  const [animatedPct, setAnimatedPct] = useState(0);
  const [showNewOptionsBanner, setShowNewOptionsBanner] = useState(false);
  const [showDocsBanner, setShowDocsBanner] = useState(false);
  const [showLogbookBanner, setShowLogbookBanner] = useState(false);
  // Career tab UI state
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [showAllSeniorRoles, setShowAllSeniorRoles] = useState(false);
  const [hoursBannerDismissed, setHoursBannerDismissed] = useState(
    () => typeof window !== "undefined" &&
      window.localStorage.getItem("uxo_career_hours_banner_dismissed") === "1"
  );
  // Cert Tracker — remember whether unset certs are expanded
  const [showUnsetCerts, setShowUnsetCerts] = useState(
    () => typeof window !== "undefined" &&
      window.localStorage.getItem("uxo_certs_show_unset") === "1"
  );

  // My Applications state
  const [myApps, setMyApps] = useState([]);
  const [myAppsLoading, setMyAppsLoading] = useState(false);
  const [unreadAppsCount, setUnreadAppsCount] = useState(0);

  // Messages inbox state
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openThread, setOpenThread] = useState(null); // employer_profile_id of open conversation
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);

  // Invitations state: { "employerProfileId-jobPostId": "pending"|"accepted"|"declined" }
  const [invitations, setInvitations] = useState({});
  const [inviteUpdating, setInviteUpdating] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    if (accountType === 'employer' || user?.user_metadata?.account_type === 'employer') {
      window.location.href = '/employer-dashboard';
      return;
    }
    loadDashboard();
  }, [user, accountType]); // eslint-disable-line

  const loadDashboard = useCallback(async () => {
    setLoading(true);

    const [{ data: prof }, { data: jobData }] = await Promise.all([
      supabase
        .from("tech_profiles")
        .select("*")
        .eq("user_id", user.id)
        .single(),
      supabase
        .from("job_posts")
        .select("*")
        .eq("status", "published")
        .order("created_at", { ascending: false }),
    ]);

    setProfile(prof || null);
    setJobs(jobData || []);

    if (prof) {
      // Check for new profile options the tech hasn't responded to
      if (hasNewOptions(prof) && !isNewOptionsBannerDismissed()) {
        setShowNewOptionsBanner(true);
      }

      // Check if user has any uploaded documents
      const { count: docCount } = await supabase
        .from('tech_documents')
        .select('id', { count: 'exact', head: true })
        .eq('tech_profile_id', prof.id);
      if (docCount === 0 && !localStorage.getItem('uxo_docs_banner_dismissed')) {
        setShowDocsBanner(true);
      }

      // Check if tech has logbook but hasn't uploaded one
      if (prof.has_logbook) {
        const { count: logbookCount } = await supabase
          .from('tech_documents')
          .select('id', { count: 'exact', head: true })
          .eq('tech_profile_id', prof.id)
          .eq('document_type', 'logbook');
        if (logbookCount === 0 && !localStorage.getItem('uxo_logbook_banner_dismissed')) {
          setShowLogbookBanner(true);
        }
      }

      const notifs = await loadActiveNotifications(prof);
      setNotifications(notifs);

      // Send cert expiry email if any certs expire within 60 days (edge function enforces one per day)
      const physExpiry = prof.physical_date
        ? new Date(new Date(prof.physical_date).setFullYear(
            new Date(prof.physical_date).getFullYear() + 1
          )).toISOString().split('T')[0]
        : null;
      const expiringCerts = [
        { cert_type: 'hazwoper_8', expiry: prof.hazwoper_8_expiry },
        { cert_type: 'first_aid_cpr', expiry: prof.first_aid_cpr_expiry },
        { cert_type: 'state_license', expiry: prof.state_license_expiry },
        { cert_type: 'physical', expiry: physExpiry },
      ].filter(c => {
        if (!c.expiry) return false;
        const d = Math.ceil((new Date(c.expiry) - new Date()) / 86400000);
        return d <= 60;
      }).map(c => ({
        cert_type: c.cert_type,
        days: Math.ceil((new Date(c.expiry) - new Date()) / 86400000),
        expiry_date: c.expiry,
      }));

      if (expiringCerts.length > 0) {
        supabase.functions.invoke('send-cert-expiry-email', {
          body: { tech_profile_id: prof.id, expiring_certs: expiringCerts },
        }).catch(() => {});
      }
    }
    setLoading(false);
  }, [user]);

  const dismissNotification = async (id) => {
    setDismissing(id);
    await supabase
      .from("notifications")
      .update({ dismissed_at: new Date().toISOString() })
      .eq("id", id);
    setNotifications(n => n.filter(x => x.id !== id));
    setDismissing(null);
  };

  // Fetch My Applications
  const fetchMyApps = useCallback(async () => {
    if (!profile) return;
    setMyAppsLoading(true);
    const { data } = await supabase
      .from('applications')
      .select('id, job_id, applicant_name, status, created_at, updated_at, tech_last_viewed_at, job_posts(title, company, location)')
      .eq('tech_profile_id', profile.id)
      .order('created_at', { ascending: false });
    setMyApps(data || []);
    setMyAppsLoading(false);
  }, [profile]);

  // Count applications with unseen status updates (updated_at > tech_last_viewed_at)
  const fetchUnreadAppsCount = useCallback(async () => {
    if (!profile) return;
    const { data } = await supabase
      .from('applications')
      .select('updated_at, tech_last_viewed_at')
      .eq('tech_profile_id', profile.id);
    const count = (data || []).filter(a =>
      !a.tech_last_viewed_at || (a.updated_at && new Date(a.updated_at) > new Date(a.tech_last_viewed_at))
    ).length;
    setUnreadAppsCount(count);
  }, [profile]);

  // Mark all of this tech's applications as viewed
  const markAppsViewed = useCallback(async () => {
    if (!profile) return;
    await supabase.from('applications')
      .update({ tech_last_viewed_at: new Date().toISOString() })
      .eq('tech_profile_id', profile.id);
    setUnreadAppsCount(0);
  }, [profile]);

  // Fetch Messages inbox (new messages table)
  const fetchMessages = useCallback(async () => {
    if (!profile) return;
    setMessagesLoading(true);
    const { data } = await supabase
      .from('messages')
      .select('id, employer_profile_id, sender_type, message, is_read, created_at, employer_profiles(id, company_name, contact_name)')
      .eq('tech_profile_id', profile.id)
      .order('created_at', { ascending: false });
    setMessages(data || []);
    setUnreadCount((data || []).filter(m => m.sender_type === 'employer' && !m.is_read).length);
    setMessagesLoading(false);
  }, [profile]);

  // Fetch unread message count (for badge, independent of full message fetch)
  const fetchUnreadMsgCount = useCallback(async () => {
    if (!profile) return;
    const { count } = await supabase.from('messages').select('id', { count: 'exact', head: true })
      .eq('tech_profile_id', profile.id).eq('sender_type', 'employer').eq('is_read', false);
    setUnreadCount(count || 0);
  }, [profile]);

  // Fetch invitations for this tech (to show accept/decline status)
  const fetchInvitations = useCallback(async () => {
    if (!profile) return;
    const { data } = await supabase
      .from('invitations')
      .select('employer_profile_id, job_post_id, status')
      .eq('tech_profile_id', profile.id);
    const map = {};
    (data || []).forEach(inv => { map[`${inv.employer_profile_id}-${inv.job_post_id}`] = inv.status; });
    setInvitations(map);
  }, [profile]);

  // Update invitation status (accept or decline) and notify employer
  const updateInvitationStatus = async (employerProfileId, jobPostId, newStatus) => {
    const key = `${employerProfileId}-${jobPostId}`;
    setInviteUpdating(key);
    await supabase
      .from('invitations')
      .update({ status: newStatus })
      .eq('tech_profile_id', profile.id)
      .eq('employer_profile_id', employerProfileId)
      .eq('job_post_id', jobPostId);
    setInvitations(prev => ({ ...prev, [key]: newStatus }));

    // Fetch job title for the notification message
    const { data: job } = await supabase.from('job_posts').select('title').eq('id', jobPostId).single();
    const jobTitle = job?.title || 'a position';
    const techName = profile.name || 'A technician';

    if (newStatus === 'accepted') {
      // In-app message to employer
      await supabase.from('messages').insert({
        employer_profile_id: employerProfileId,
        tech_profile_id: profile.id,
        sender_type: 'tech',
        message: `${techName} has accepted your invitation to apply for ${jobTitle}. Visit your employer hub to review their profile.`,
      });
      // Email notification to employer (fire and forget)
      supabase.functions.invoke('send-invite-notification', {
        body: { tech_profile_id: profile.id, employer_profile_id: employerProfileId, job_post_id: jobPostId, type: 'invite_accepted' },
      }).catch(() => {});
    } else if (newStatus === 'declined') {
      // In-app message to employer (no email for decline)
      await supabase.from('messages').insert({
        employer_profile_id: employerProfileId,
        tech_profile_id: profile.id,
        sender_type: 'tech',
        message: `${techName} has declined your invitation for ${jobTitle}.`,
      });
    }

    setInviteUpdating(null);
  };

  useEffect(() => { if (activeSection === 'apps' && profile) { fetchMyApps(); markAppsViewed(); } }, [activeSection, fetchMyApps, markAppsViewed, profile]);
  useEffect(() => { if (activeSection === 'messages' && profile) { fetchMessages(); fetchInvitations(); } }, [activeSection, fetchMessages, fetchInvitations, profile]);

  // Fetch published resources when the Resources tab opens
  const fetchResources = useCallback(async () => {
    setResourcesLoading(true);
    const { data } = await supabase
      .from('resources')
      .select('*')
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });
    setResources(data || []);
    setResourcesLoading(false);
  }, []);

  useEffect(() => {
    if (SHOW_RESOURCES && activeSection === 'resources') fetchResources();
  }, [activeSection, fetchResources]);

  // Fetch all badge counts on profile load
  useEffect(() => {
    if (!profile) return;
    fetchUnreadMsgCount();
    fetchUnreadAppsCount();
  }, [profile, fetchUnreadMsgCount, fetchUnreadAppsCount]);

  // Poll all badge counts every 30 seconds
  useEffect(() => {
    if (!profile) return;
    const interval = setInterval(() => {
      fetchUnreadMsgCount();
      fetchUnreadAppsCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [profile, fetchUnreadMsgCount, fetchUnreadAppsCount]);

  const markThreadRead = async (employerProfileId) => {
    const unreadIds = messages
      .filter(m => m.employer_profile_id === employerProfileId && m.sender_type === 'employer' && !m.is_read)
      .map(m => m.id);
    if (unreadIds.length === 0) return;
    await supabase.from('messages').update({ is_read: true }).in('id', unreadIds);
    setMessages(prev => prev.map(m => unreadIds.includes(m.id) ? { ...m, is_read: true } : m));
    setUnreadCount(prev => Math.max(0, prev - unreadIds.length));
  };

  const handleTechReply = async (employerProfileId) => {
    if (!replyText.trim() || !profile) return;
    setReplySending(true);
    await supabase.from('messages').insert({
      employer_profile_id: employerProfileId,
      tech_profile_id: profile.id,
      sender_type: 'tech',
      message: replyText.trim(),
    });
    setReplyText('');
    setReplySending(false);
    fetchMessages();
  };

  // Animate progress bar when career tab is active
  useEffect(() => {
    if (activeSection === "career" && profile) {
      setAnimatedPct(0);
      const h = profile.total_field_hours || 0;
      const verStatus = profile.logbook_verification_status || 'unverified';
      const vSup = profile.logbook_verified_supervisory_hours;
      const sup = (vSup != null && verStatus === 'verified') ? vSup : (profile.supervisory_hours || 0);
      const cl = getLevel(h);
      const nl = HOURS_LEVELS[cl.index + 1] || null;
      const hasO10 = !!profile.osha_10_construction;
      const qv = profile.quality_credential && profile.quality_credential !== 'None' && profile.quality_credential_status === 'verified';
      const fullyMaxed = h >= 18800 && sup >= 9400 && hasO10 && qv;
      let targetPct;
      if (nl) {
        targetPct = Math.min(100, Math.round(((h - cl.min) / (nl.min - cl.min)) * 100));
      } else if (fullyMaxed) {
        targetPct = 100;
      } else if (sup < 3760) {
        targetPct = Math.min(100, Math.round((sup / 3760) * 100));
      } else if (sup < 9400) {
        targetPct = Math.min(100, Math.round((sup / 9400) * 100));
      } else {
        targetPct = 100;
      }
      const timer = setTimeout(() => setAnimatedPct(targetPct), 50);
      return () => clearTimeout(timer);
    }
  }, [activeSection, profile]);

  if (loading) return (
    <div style={s.root}>
      <div style={{ color: "#7a7570", padding: 40, textAlign: "center" }}>
        Loading dashboard...
      </div>
    </div>
  );

  if (!profile) {
    // Employer accounts should never see this screen — hard redirect out
    if (accountType === 'employer' || user?.user_metadata?.account_type === 'employer') {
      window.location.href = '/employer-dashboard';
      return null;
    }
    return (
      <div style={s.root}>
        <div style={s.emptyCard}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>{"\uD83E\uDE96"}</div>
          <h2 style={s.emptyTitle}>No Tech Profile Found</h2>
          <p style={s.emptyMsg}>Create your technician profile to access your dashboard.</p>
          <button style={s.btnPrimary} onClick={() => navigate("/create-profile")}>
            Create Profile {"\u2192"}
          </button>
        </div>
      </div>
    );
  }

  const hours = profile.total_field_hours || 0;
  const supHrsGlobal = profile.supervisory_hours || 0;
  const hasOsha10 = !!profile.osha_10_construction;
  const qualityVerified = profile.quality_credential && profile.quality_credential !== 'None' && profile.quality_credential_status === 'verified';
  const currentLevel  = getLevel(hours);
  const nextLevel     = HOURS_LEVELS[currentLevel.index + 1] || null;

  // SUXOS = true maximum: 18,800+ MEC, 9,400+ supervisory, OSHA 10, quality credential verified
  const isTrulyMaxed = hours >= 18800 && supHrsGlobal >= 9400 && hasOsha10 && qualityVerified;

  const scoredJobs = jobs
    .map(job => ({ ...job, ...scoreJob(job, profile) }))
    .filter(j => j.qualified)
    .filter(j => {
      if (profile.job_role_preference !== 'specific') return true;
      const wanted = profile.specific_roles || [];
      if (wanted.length === 0) return true;
      const title = (j.title || '').toLowerCase();
      return wanted.some(role => title.toLowerCase().includes(role.toLowerCase()));
    })
    .sort((a, b) => b.matchPct - a.matchPct);

  const CERT_LABELS = {
    hazwoper_8:        "HAZWOPER 8-Hour Refresher",
    dod_cert:          "UXO Certification",
    first_aid_cpr:     "First Aid / CPR",
    state_license:     "State License",
    physical:          "Annual Physical",
    logbook_verified:  "Logbook Documented",
    logbook_rejected:  "Logbook Review",
    quality_cert_verified: "Quality Credential Documented",
    quality_cert_rejected: "Quality Credential Review",
  };

  // Build expiring cert alerts for Notifications tab (certs expiring within 60 days)
  const certAlerts = [];
  const addAlert = (label, expiryDate) => {
    if (!expiryDate) return;
    const days = daysUntil(expiryDate);
    if (days !== null && days <= 60) {
      certAlerts.push({ label, expiryDate, days });
    }
  };
  addAlert("HAZWOPER 8-Hour Refresher", profile.hazwoper_8_expiry);
  addAlert("First Aid / CPR", profile.first_aid_cpr_expiry);
  addAlert("State License", profile.state_license_expiry);
  addAlert("Annual Physical", profile.physical_date
    ? new Date(new Date(profile.physical_date).setFullYear(
        new Date(profile.physical_date).getFullYear() + 1
      )).toISOString().split("T")[0]
    : null);
  certAlerts.sort((a, b) => a.days - b.days);

  const notifCount = notifications.length + certAlerts.length;

  const navItems = [
    { id: "jobs",     label: "Matches",          icon: "\uD83C\uDFAF" },
    { id: "apps",     label: "Applications",    icon: "\uD83D\uDCC4", badge: unreadAppsCount > 0 ? unreadAppsCount : null },
    { id: "messages", label: "Messages",        icon: "\uD83D\uDCE8", badge: unreadCount > 0 ? unreadCount : null },
    { id: "docs",     label: "Documents",        icon: "\uD83D\uDCC1" },
    { id: "certs",    label: "Certs",            icon: "\uD83D\uDCCB" },
    { id: "career",   label: "Career",          icon: "\uD83D\uDCC8" },
    ...(SHOW_RESOURCES ? [{ id: "resources", label: "Resources", icon: "\uD83D\uDCDA" }] : []),
    { id: "notifs",   label: "Alerts",           icon: "\uD83D\uDD14", badge: notifCount > 0 ? notifCount : null },
  ];

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div style={s.headerInner}>
          <div>
            <div style={s.headerLabel}>Tech Dashboard</div>
            <div style={s.headerName}>{profile.name}</div>
          </div>
          <div style={s.headerRight}>
            <button style={s.editProfileBtn} onClick={() => navigate("/create-profile")}>Edit Profile</button>
            <button style={s.backBtn} onClick={() => navigate("/support")}>Need help?</button>
            <button style={s.backBtn} onClick={() => navigate("/")}>{"\u2190"} Home</button>
          </div>
        </div>
      </div>

      {/* ── New options banner ── */}
      {showNewOptionsBanner && (
        <div style={s.notifWrap}>
          <div style={s.newOptionsBanner}>
            <span style={{ flex: 1 }}>
              Your profile has new options available. Update your profile to make sure employers see your full qualifications.
            </span>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button style={s.newOptionsBtnUpdate} onClick={() => navigate("/create-profile")}>
                Update Profile
              </button>
              <button style={s.newOptionsBtnDismiss} onClick={() => { dismissNewOptionsBanner(); setShowNewOptionsBanner(false); }}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Document upload banner ── */}
      {showDocsBanner && (
        <div style={s.notifWrap}>
          <div style={{
            background: '#0a1a2a', border: '1px solid #60a5fa44', borderRadius: 8,
            padding: '14px 16px', fontSize: 14, color: '#60a5fa',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', lineHeight: 1.5,
          }}>
            <span style={{ flex: 1 }}>
              Complete your profile — upload your certifications and resume in the My Documents tab.
            </span>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button style={{
                background: '#60a5fa', border: 'none', color: '#0d0f10', cursor: 'pointer',
                padding: '6px 14px', fontSize: 13, borderRadius: 6, fontFamily: 'inherit', fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }} onClick={() => { setActiveSection('docs'); setShowDocsBanner(false); }}>
                Upload Documents
              </button>
              <button style={{
                background: 'none', border: '1px solid #60a5fa44', color: '#60a5fa', cursor: 'pointer',
                padding: '6px 14px', fontSize: 13, borderRadius: 6, fontFamily: 'inherit', whiteSpace: 'nowrap',
              }} onClick={() => { localStorage.setItem('uxo_docs_banner_dismissed', '1'); setShowDocsBanner(false); }}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Logbook upload banner ── */}
      {showLogbookBanner && (
        <div style={s.notifWrap}>
          <div style={{
            background: '#1a1408', border: '1px solid #d9770644', borderRadius: 8,
            padding: '14px 16px', fontSize: 14, color: '#d97706',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', lineHeight: 1.5,
          }}>
            <span style={{ flex: 1 }}>
              New: Upload your signed logbook to have your hours documented by UXOHire. Documented hours give employers more confidence in your qualifications.
            </span>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button style={{
                background: '#d97706', border: 'none', color: '#0d0f10', cursor: 'pointer',
                padding: '6px 14px', fontSize: 13, borderRadius: 6, fontFamily: 'inherit',
                fontWeight: 'bold', whiteSpace: 'nowrap',
              }} onClick={() => { setActiveSection('docs'); setShowLogbookBanner(false); }}>
                Upload Logbook
              </button>
              <button style={{
                background: 'none', border: '1px solid #d9770644', color: '#d97706', cursor: 'pointer',
                padding: '6px 14px', fontSize: 13, borderRadius: 6, fontFamily: 'inherit', whiteSpace: 'nowrap',
              }} onClick={() => { localStorage.setItem('uxo_logbook_banner_dismissed', '1'); setShowLogbookBanner(false); }}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Notification banners (top-level urgent alerts) ── */}
      {notifications.length > 0 && (
        <div style={s.notifWrap}>
          {notifications.map(n => {
            const days = daysUntil(n.expiry_date);
            const expired = days !== null && days < 0;
            const urgent = days !== null && days <= 30;
            return (
              <div key={n.id} style={{ ...s.notifBanner, ...(urgent || expired ? s.notifBannerUrgent : {}) }}>
                <span>
                  {urgent || expired ? "\uD83D\uDEA8" : "\u26A0\uFE0F"} Your <strong>{CERT_LABELS[n.cert_type] || n.cert_type}</strong>
                  {expired ? ` expired ${Math.abs(days)} days ago` : days !== null ? ` expires in ${days} days` : " is expiring soon"} ({n.expiry_date}).
                </span>
                <button
                  style={s.notifDismiss}
                  onClick={() => dismissNotification(n.id)}
                  disabled={dismissing === n.id}
                >
                  {dismissing === n.id ? "\u2026" : "\u2715"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Nav tabs ── */}
      <div style={s.tabBar} className="td-tab-bar">
        {navItems.map(item => (
          <button
            key={item.id}
            className={"td-tab" + (activeSection === item.id ? " td-tab-active" : "")}
            style={{ ...s.tab, ...(activeSection === item.id ? s.tabActive : {}), position: 'relative' }}
            onClick={() => setActiveSection(item.id)}
          >
            <span style={s.tabIcon}>{item.icon}</span>
            <span className="td-tab-label" style={s.tabLabel}>{item.label}</span>
            {item.badge && (
              <span style={{
                background: '#d97706', color: '#0d0f10', fontSize: 10, fontWeight: 'bold',
                borderRadius: 8, padding: '1px 6px', marginLeft: 4
              }}>
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={s.body} className="td-body">

        {/* ═══════════════════════════════════════════════════
            TAB 1 — JOB MATCH DASHBOARD
        ═══════════════════════════════════════════════════ */}
        {activeSection === "jobs" && (
          <div>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>Jobs You Qualify For</h2>
              <span style={s.sectionSub}>{scoredJobs.length} matched listings</span>
            </div>
            {profile.job_role_preference === 'specific' && (profile.specific_roles || []).length > 0 && (
              <div style={{ background: '#1a1408', border: '1px solid #d9770633', borderRadius: 8,
                padding: '10px 14px', fontSize: 13, color: '#d97706', marginBottom: 16 }}>
                {"\uD83C\uDFAF"} Filtering for: {profile.specific_roles.join(', ')}
              </div>
            )}

            {scoredJobs.length === 0 && (
              <div style={s.emptyState}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{"\uD83D\uDD0D"}</div>
                <p>No current listings match your certifications.</p>
                <p style={{ color: "#555", fontSize: 13 }}>
                  Add more certs to your profile or check back when new jobs post.
                </p>
              </div>
            )}

            <div style={s.jobGrid}>
              {scoredJobs.map(job => (
                <div key={job.id} style={s.jobCard}>
                  <div style={s.jobCardTop}>
                    <div>
                      <div style={s.jobCompany}>{job.company}</div>
                      <div style={s.jobTitle}>{job.title}</div>
                      <div style={s.jobMeta}>
                        <span>{"\uD83D\uDCCD"} {job.location}</span>
                        {job.salary && <span>{"\uD83D\uDCB0"} {job.salary}</span>}
                        <span>{"\uD83D\uDDC2"} {job.type}</span>
                      </div>
                    </div>
                    <MatchBadge pct={job.matchPct} />
                  </div>

                  {job.required_certs?.length > 0 && (
                    <div style={s.matchSection}>
                      <div style={s.matchLabel}>Required</div>
                      <div style={s.certRow}>
                        {job.required_certs.map(c => (
                          <span key={c} style={{
                            ...s.certChip,
                            ...(job.metRequired.includes(c) ? s.certChipMet : s.certChipMissed)
                          }}>
                            {job.metRequired.includes(c) ? "\u2713" : "\u2717"} {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {job.preferred_certs?.length > 0 && (
                    <div style={s.matchSection}>
                      <div style={s.matchLabel}>Preferred</div>
                      <div style={s.certRow}>
                        {job.preferred_certs.map(c => (
                          <span key={c} style={{
                            ...s.certChip,
                            ...(job.metPreferred.includes(c) ? s.certChipPref : s.certChipNeutral)
                          }}>
                            {job.metPreferred.includes(c) ? "\u2713" : "\u25CB"} {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <button style={s.applyBtn} onClick={() => navigate(`/?job=${job.id}`)}>
                    View & Apply {"\u2192"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
            TAB — MY APPLICATIONS
        ═══════════════════════════════════════════════════ */}
        {activeSection === "apps" && (
          <div>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>My Applications</h2>
              <span style={s.sectionSub}>Jobs you've applied to</span>
            </div>
            {myAppsLoading && <p style={{ color: '#7a7570' }}>Loading applications...</p>}
            {!myAppsLoading && myApps.length === 0 && (
              <div style={s.emptyState}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{"\uD83D\uDCC4"}</div>
                <p style={{ color: '#e8e4dc', fontSize: 15 }}>You haven't applied to any jobs yet.</p>
                <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
                  Browse open positions to find your next opportunity.
                </p>
                <button style={s.btnPrimary} onClick={() => setActiveSection('jobs')}>
                  Browse Jobs {"\u2192"}
                </button>
              </div>
            )}
            {!myAppsLoading && myApps.length > 0 && (
              <div style={s.jobGrid}>
                {myApps.map(app => {
                  const job = app.job_posts;
                  const statusColor = app.status === 'accepted' ? '#4ade80'
                    : app.status === 'under_review' ? '#fbbf24'
                    : app.status === 'rejected' ? '#f87171' : '#9a9490';
                  const statusBg = app.status === 'accepted' ? '#0a2a1a'
                    : app.status === 'under_review' ? '#2a1e08'
                    : app.status === 'rejected' ? '#2a0a0a' : '#1a1c1e';
                  const statusLabel = app.status === 'accepted' ? 'Accepted'
                    : app.status === 'under_review' ? 'Under Review'
                    : app.status === 'rejected' ? 'Rejected' : 'Submitted';
                  return (
                    <div key={app.id} style={s.jobCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={s.jobCompany}>{job?.company || 'Company'}</div>
                          <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 4 }}>{job?.title || 'Job Post'}</div>
                          <div style={{ fontSize: 12, color: '#7a7570' }}>
                            {job?.location && <span>{job.location} {"\u00B7"} </span>}
                            Applied {new Date(app.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <span style={{
                          background: statusBg, color: statusColor, fontSize: 11, padding: '3px 10px',
                          borderRadius: 12, fontWeight: 'bold', border: `1px solid ${statusColor}33`,
                          whiteSpace: 'nowrap',
                        }}>
                          {"\u25CF"} {statusLabel}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
            TAB — MESSAGES INBOX (threaded)
        ═══════════════════════════════════════════════════ */}
        {activeSection === "messages" && (
          <div>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>Messages</h2>
              <span style={s.sectionSub}>Conversations with employers</span>
            </div>
            {messagesLoading && <p style={{ color: '#7a7570' }}>Loading messages...</p>}
            {!messagesLoading && messages.length === 0 && (
              <div style={s.emptyState}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{"\uD83D\uDCE8"}</div>
                <p style={{ color: '#e8e4dc', fontSize: 15 }}>No messages yet.</p>
                <p style={{ color: '#555', fontSize: 13 }}>
                  When employers contact you, their messages will appear here.
                </p>
              </div>
            )}
            {!messagesLoading && messages.length > 0 && !openThread && (() => {
              // Group messages by employer into conversation threads
              const threads = {};
              messages.forEach(m => {
                const epId = m.employer_profile_id;
                if (!threads[epId]) {
                  threads[epId] = {
                    employerProfileId: epId,
                    companyName: m.employer_profiles?.company_name || 'Employer',
                    contactName: m.employer_profiles?.contact_name || '',
                    messages: [],
                    hasUnread: false,
                    lastDate: m.created_at,
                  };
                }
                threads[epId].messages.push(m);
                if (m.sender_type === 'employer' && !m.is_read) threads[epId].hasUnread = true;
              });
              const threadList = Object.values(threads).sort((a, b) => new Date(b.lastDate) - new Date(a.lastDate));
              return (
                <div style={s.jobGrid}>
                  {threadList.map(thread => {
                    const lastMsg = thread.messages[0];
                    return (
                      <div key={thread.employerProfileId} style={{
                        ...s.jobCard, cursor: 'pointer',
                        borderLeft: thread.hasUnread ? '3px solid #d97706' : '3px solid transparent',
                      }} onClick={() => { setOpenThread(thread.employerProfileId); markThreadRead(thread.employerProfileId); }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                          <div>
                            <div style={{ fontWeight: 'bold', fontSize: 15, color: '#e8e4dc' }}>{thread.companyName}</div>
                            {thread.contactName && <div style={{ fontSize: 12, color: '#7a7570' }}>{thread.contactName}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {thread.hasUnread && <span style={{ background: '#1a1408', color: '#d97706', fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 'bold', border: '1px solid #d9770633' }}>New</span>}
                            <span style={{ fontSize: 11, color: '#7a7570' }}>{new Date(thread.lastDate).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <p style={{ color: '#9a9490', fontSize: 13, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lastMsg.message.startsWith('[INVITATION') && (
                            <span style={{ color: '#d97706', fontWeight: 'bold' }}>Invitation: </span>
                          )}
                          {lastMsg.sender_type === 'tech' ? 'You: ' : ''}{lastMsg.message.replace(/^\[INVITATION(?::[^\]]+)?\]\s*/, '')}
                        </p>
                        <div style={{ fontSize: 11, color: '#7a7570', marginTop: 4 }}>{thread.messages.length} message{thread.messages.length !== 1 ? 's' : ''}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {/* Open thread view */}
            {!messagesLoading && openThread && (() => {
              const threadMsgs = messages.filter(m => m.employer_profile_id === openThread).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
              const companyName = threadMsgs[0]?.employer_profiles?.company_name || 'Employer';
              return (
                <div>
                  <button style={{ background: 'none', border: 'none', color: '#d97706', cursor: 'pointer', fontSize: 14, padding: '0 0 16px', fontFamily: 'inherit' }}
                    onClick={() => { setOpenThread(null); setReplyText(''); }}>{"\u2190"} Back to Messages</button>
                  <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16 }}>{companyName}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                    {threadMsgs.map(msg => {
                      const isInvite = msg.message.startsWith('[INVITATION');
                      const inviteJobId = isInvite ? (msg.message.match(/^\[INVITATION:([^\]]+)\]/) || [])[1] : null;
                      const displayMsg = isInvite ? msg.message.replace(/^\[INVITATION(?::[^\]]+)?\]\s*/, '') : msg.message;
                      return (
                        <div key={msg.id} style={{
                          background: isInvite ? '#1a1408' : msg.sender_type === 'tech' ? '#0a1a2a' : '#111316',
                          border: `1px solid ${isInvite ? '#d97706' : msg.sender_type === 'tech' ? '#1a3a5a33' : '#1e2022'}`,
                          borderRadius: 10, padding: '12px 16px',
                          marginLeft: msg.sender_type === 'tech' ? 40 : 0,
                          marginRight: msg.sender_type === 'tech' ? 0 : 40,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ fontSize: 12, fontWeight: 'bold', color: msg.sender_type === 'tech' ? '#60a5fa' : '#d97706' }}>
                                {msg.sender_type === 'tech' ? 'You' : companyName}
                              </span>
                              {isInvite && (
                                <span style={{
                                  fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                                  background: '#d97706', color: '#0d0f10',
                                }}>Invitation</span>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: '#7a7570' }}>{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                          <p style={{ color: '#b0aca6', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{displayMsg}</p>
                          {isInvite && inviteJobId && (() => {
                            const invKey = `${msg.employer_profile_id}-${inviteJobId}`;
                            const invStatus = invitations[invKey];
                            const isUpdating = inviteUpdating === invKey;
                            return (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                                <button style={{
                                  background: '#d97706', border: 'none', color: '#0d0f10', padding: '6px 16px',
                                  borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                                  fontWeight: 'bold',
                                }} onClick={() => navigate(`/?job=${inviteJobId}`)}>
                                  View Job {"\u2192"}
                                </button>
                                {invStatus === 'accepted' && (
                                  <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 'bold' }}>{"\u2713"} Accepted</span>
                                )}
                                {invStatus === 'declined' && (
                                  <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 'bold' }}>{"\u2717"} Declined</span>
                                )}
                                {(!invStatus || invStatus === 'pending') && (
                                  <>
                                    <button style={{
                                      background: '#0a2a1a', border: '1px solid #22c55e44', color: '#22c55e', padding: '5px 14px',
                                      borderRadius: 6, cursor: isUpdating ? 'default' : 'pointer', fontFamily: 'inherit',
                                      fontSize: 12, fontWeight: 'bold', opacity: isUpdating ? 0.5 : 1,
                                    }} disabled={isUpdating} onClick={() => updateInvitationStatus(msg.employer_profile_id, inviteJobId, 'accepted')}>
                                      {isUpdating ? '...' : 'Accept'}
                                    </button>
                                    <button style={{
                                      background: '#1a0a0a', border: '1px solid #ef444444', color: '#ef4444', padding: '5px 14px',
                                      borderRadius: 6, cursor: isUpdating ? 'default' : 'pointer', fontFamily: 'inherit',
                                      fontSize: 12, fontWeight: 'bold', opacity: isUpdating ? 0.5 : 1,
                                    }} disabled={isUpdating} onClick={() => updateInvitationStatus(msg.employer_profile_id, inviteJobId, 'declined')}>
                                      {isUpdating ? '...' : 'Decline'}
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ background: '#111316', border: '1px solid #1e2022', borderRadius: 10, padding: '16px' }}>
                    <textarea style={{
                      background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 6, color: '#e8e4dc',
                      padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none',
                      width: '100%', boxSizing: 'border-box', minHeight: 70, resize: 'vertical',
                    }} placeholder="Write a reply..." value={replyText} onChange={e => setReplyText(e.target.value)} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                      <button style={s.btnPrimary} onClick={() => handleTechReply(openThread)} disabled={replySending || !replyText.trim()}>
                        {replySending ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════
            TAB — MY DOCUMENTS
        ═══════════════════════════════════════════════════ */}
        {activeSection === "docs" && (
          <DocumentsTab profile={profile} user={user} />
        )}

        {/* ═══════════════════════════════════════════════════
            TAB 2 — CERTIFICATION TRACKER
        ═══════════════════════════════════════════════════ */}
        {activeSection === "certs" && (() => {
          const roles = profile.dod_certs || [];
          const hasUXOSO  = roles.includes("UXO Safety Officer / UXOSO");
          const hasUXOQCS = roles.includes("UXO Quality Control");
          const hasSUXOS  = roles.includes("SUXOS - Senior UXO Supervisor");

          // Which selected senior roles require a given cert (only when the tech holds those roles)
          const reqFor = (which) => {
            const r = [];
            if (which === 'sup8' || which === 'osha10') {
              if (hasUXOSO) r.push("UXOSO");
              if (hasSUXOS) r.push("SUXOS");
            }
            if (which === 'quality') {
              if (hasUXOQCS) r.push("UXOQCS");
              if (hasSUXOS) r.push("SUXOS");
            }
            return r.length ? r : null;
          };

          const qualityHeld = !!(profile.quality_credential && profile.quality_credential !== 'None');
          const physicalExpiry = profile.physical_date
            ? new Date(new Date(profile.physical_date).setFullYear(
                new Date(profile.physical_date).getFullYear() + 1
              )).toISOString().split("T")[0]
            : null;

          // Build cert definitions with derived held / required-for-role state
          const defs = [
            { key: "haz40", held: !!profile.hazwoper_40,
              el: <CertCard key="haz40" label="HAZWOPER 40-Hour" held={profile.hazwoper_40}
                    issueDate={profile.hazwoper_40_date} lifetime={true}
                    lifetimeText="Lifetime / No expiration" /> },

            { key: "haz8", held: !!profile.hazwoper_8,
              el: <CertCard key="haz8" label="HAZWOPER 8-Hour Refresher" held={profile.hazwoper_8}
                    issueDate={profile.hazwoper_8_date} expiryDate={profile.hazwoper_8_expiry}
                    renewalNote="Renews annually" /> },

            { key: "haz8sup", held: !!profile.hazwoper_8_supervisor, requiredFor: reqFor('sup8'),
              el: <CertCard key="haz8sup" label="HAZWOPER 8-Hour Supervisor"
                    held={profile.hazwoper_8_supervisor} lifetime={profile.hazwoper_8_supervisor}
                    requiredFor={reqFor('sup8')} /> },

            { key: "firstaid", held: !!profile.first_aid_cpr,
              el: <CertCard key="firstaid" label="First Aid / CPR" held={profile.first_aid_cpr}
                    issueDate={profile.first_aid_cpr_date} expiryDate={profile.first_aid_cpr_expiry}
                    renewalNote="Renews every 2 years" /> },

            { key: "osha10", held: !!profile.osha_10_construction, requiredFor: reqFor('osha10'),
              el: <CertCard key="osha10" label="10-Hour OSHA Construction Safety"
                    held={profile.osha_10_construction} lifetime={profile.osha_10_construction}
                    lifetimeText="Lifetime / No expiration" requiredFor={reqFor('osha10')} /> },

            { key: "quality", held: qualityHeld, requiredFor: qualityHeld ? null : reqFor('quality'),
              el: qualityHeld
                ? <CertCard key="quality" label={`Quality Credential — ${profile.quality_credential}`}
                    held={true} lifetime={true} lifetimeText="Lifetime" />
                : <CertCard key="quality" label="Quality Credential" held={false}
                    requiredFor={reqFor('quality')} /> },

            ...roles.map(cert => ({ key: `role-${cert}`, held: true,
              el: <CertCard key={`role-${cert}`} label={cert} held={true} lifetime={true}
                    lifetimeText="Active" /> })),
            ...(roles.length === 0 ? [{ key: "uxo-none", held: false,
              el: <CertCard key="uxo-none" label="UXO Certification" held={false} /> }] : []),

            { key: "statelic", held: !!profile.state_license,
              el: profile.state_license
                ? <CertCard key="statelic" label={`State License — ${profile.state_license}`}
                    held={true} expiryDate={profile.state_license_expiry} renewalNote="Varies by state" />
                : <CertCard key="statelic" label="State License" held={false} /> },

            { key: "physical", held: !!profile.physical_current,
              el: <CertCard key="physical" label="Annual Physical" held={profile.physical_current}
                    issueDate={profile.physical_date} expiryDate={physicalExpiry}
                    renewalNote="Renews annually" /> },

            { key: "clearance", held: !!profile.clearance,
              el: <CertCard key="clearance"
                    label={profile.clearance_level ? `Clearance — ${profile.clearance_level}` : "Security Clearance"}
                    held={profile.clearance} lifetime={profile.clearance} renewalNote="Managed via SF-86" /> },

            { key: "eod", held: !!profile.military_eod,
              el: <CertCard key="eod" label="EOD Background" held={profile.military_eod}
                    lifetime={profile.military_eod} /> },

            { key: "dive", held: !!profile.dive_cert,
              el: <CertCard key="dive" label="Dive Certification" held={profile.dive_cert}
                    lifetime={profile.dive_cert} /> },

            { key: "dl", held: !!profile.drivers_license,
              el: <CertCard key="dl" label="Driver's License" held={profile.drivers_license}
                    lifetime={profile.drivers_license} /> },

            { key: "cdl", held: !!profile.cdl,
              el: <CertCard key="cdl" label="CDL" held={profile.cdl} lifetime={profile.cdl} /> },
          ];

          const isHighlighted = (d) => !d.held && d.requiredFor && d.requiredFor.length > 0;
          // Active certs first, then required-but-missing highlights — both stay visible
          const mainDefs = defs
            .filter(d => d.held || isHighlighted(d))
            .sort((a, b) => (a.held === b.held ? 0 : a.held ? -1 : 1));
          const unsetDefs = defs.filter(d => !d.held && !isHighlighted(d));

          const toggleUnset = () => {
            const next = !showUnsetCerts;
            setShowUnsetCerts(next);
            try { window.localStorage.setItem("uxo_certs_show_unset", next ? "1" : "0"); } catch (e) {}
          };

          return (
            <div>
              <div style={s.sectionHeader}>
                <h2 style={s.sectionTitle}>Certification Tracker</h2>
                <span style={s.sectionSub}>Your credentials at a glance</span>
              </div>

              <div style={s.certGrid}>
                {mainDefs.map(d => d.el)}
              </div>

              {unsetDefs.length > 0 && (
                <>
                  <button style={s.unsetToggle} onClick={toggleUnset}>
                    {showUnsetCerts
                      ? `Hide unset certifications (${unsetDefs.length}) ▲`
                      : `Show unset certifications (${unsetDefs.length}) ▼`}
                  </button>
                  {showUnsetCerts && (
                    <div style={{ ...s.certGrid, marginTop: 14 }}>
                      {unsetDefs.map(d => d.el)}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* ═══════════════════════════════════════════════════
            TAB 3 — CAREER PROGRESSION
        ═══════════════════════════════════════════════════ */}
        {activeSection === "career" && (() => {
          const verifiedHrs = profile.logbook_verified_hours;
          const verificationStatus = profile.logbook_verification_status || 'unverified';
          const selfReported = hours;
          const hasVerified = verifiedHrs != null && verificationStatus === 'verified';

          const verifiedSupHrs = profile.logbook_verified_supervisory_hours;
          const hasVerifiedSup = verifiedSupHrs != null && verificationStatus === 'verified';
          const selfReportedSup = supHrsGlobal;
          const effectiveSup = hasVerifiedSup ? verifiedSupHrs : selfReportedSup;
          const effectiveMec = hasVerified ? verifiedHrs : selfReported;

          // Warning banner — self-reported exceeds verified (MEC or supervisory)
          const hoursExceedVerified =
            (hasVerified && selfReported > verifiedHrs) ||
            (hasVerifiedSup && selfReportedSup > verifiedSupHrs);
          const showHoursWarning = hoursExceedVerified && !hoursBannerDismissed;

          const checkMark = "✓";
          const crossMark = "✗";

          // Senior-role requirements (used once MEC hours are maxed at Tech III)
          const SENIOR_ROLES = [
            { name: "UXO Quality Control", mec: 15040, sup: 3760,
              extra: [{ label: "Quality credential", met: qualityVerified, need: "Required" }] },
            { name: "UXO Safety Officer / UXOSO", mec: 15040, sup: 3760,
              extra: [{ label: "OSHA 10 Construction", met: hasOsha10, need: "Required" }] },
            { name: "SUXOS - Senior UXO Supervisor", mec: 18800, sup: 9400,
              extra: [
                { label: "OSHA 10 Construction", met: hasOsha10, need: "Required" },
                { label: "Quality credential", met: qualityVerified, need: "Required" },
              ] },
          ];
          const withRoleStatus = (r) => {
            const mecMet = hours >= r.mec;
            const supMet = effectiveSup >= r.sup;
            const extraUnmet = r.extra.filter(x => !x.met).length;
            const unmet = (mecMet ? 0 : 1) + (supMet ? 0 : 1) + extraUnmet;
            return { ...r, mecMet, supMet, unmet };
          };
          const seniorWithStatus = SENIOR_ROLES.map(withRoleStatus);
          const nearestSenior = seniorWithStatus
            .filter(r => r.unmet > 0)
            .sort((a, b) => a.unmet - b.unmet)[0] || seniorWithStatus[0];

          // One checklist line: ✓/✗  Label: detail
          const checkLine = (met, label, detail) => (
            <div key={label} style={s.checkItem}>
              <span style={{ color: met ? '#4ade80' : '#f87171', fontWeight: 'bold', flexShrink: 0 }}>
                {met ? checkMark : crossMark}
              </span>
              <span style={{ color: '#cfcabf' }}>{label}:</span>
              <span style={{ color: met ? '#4ade80' : '#9a9490' }}>{detail}</span>
            </div>
          );
          const roleChecklist = (r) => (
            <div style={s.checklist}>
              {checkLine(r.mecMet, "MEC hours",
                r.mecMet ? "Met" : (r.mec - hours).toLocaleString() + " hrs needed")}
              {checkLine(r.supMet, "Supervisory hours",
                r.supMet ? "Met" : (r.sup - effectiveSup).toLocaleString() + " hrs needed")}
              {r.extra.map(x => checkLine(x.met, x.label, x.met ? "Met" : x.need))}
            </div>
          );

          return (
          <div>
            {/* ── Warning banner (dismissable) ── */}
            {showHoursWarning && (
              <div style={s.careerWarn}>
                <span style={{ flex: 1, minWidth: 140 }}>
                  Your self-reported hours exceed your documented hours. Upload an updated logbook.
                </span>
                <button style={s.careerWarnLink} onClick={() => setActiveSection('docs')}>
                  Upload Logbook
                </button>
                <button
                  style={s.careerWarnClose}
                  aria-label="Dismiss"
                  onClick={() => {
                    try { window.localStorage.setItem('uxo_career_hours_banner_dismissed', '1'); } catch (e) {}
                    setHoursBannerDismissed(true);
                  }}
                >{"✕"}</button>
              </div>
            )}

            {/* ── Pending verification note ── */}
            {verificationStatus === 'pending' && (
              <div style={s.careerPending}>
                {"⏳"} Your logbook is pending review. Please allow up to 5 business days.
              </div>
            )}

            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>Career Progression</h2>
              <span style={s.sectionSub}>Based on documented field hours</span>
            </div>

            {/* ═══ SECTION 1 — Current status ═══ */}
            <div style={s.statusCard}>
              <div style={s.statusBadge}>{currentLevel.label}</div>
              <div style={s.pillRow}>
                {/* MEC hours */}
                <div style={s.pillCol}>
                  <div style={s.pillAmber}>
                    {effectiveMec.toLocaleString()} hrs MEC
                    {hasVerified
                      ? <span style={s.pillCheckAmber}> {checkMark} Hours Documented</span>
                      : <span style={s.pillUnverified}> self-reported</span>}
                  </div>
                  {hasVerified && selfReported > verifiedHrs && (
                    <div style={s.pillSub}>{selfReported.toLocaleString()} hrs self-reported</div>
                  )}
                </div>
                {/* Supervisory hours */}
                <div style={s.pillCol}>
                  <div style={s.pillTeal}>
                    {effectiveSup.toLocaleString()} supervisory hrs
                    {hasVerifiedSup
                      ? <span style={s.pillCheckTeal}> {checkMark} Supervisory Hours Documented</span>
                      : (selfReportedSup > 0 ? <span style={s.pillUnverified}> self-reported</span> : null)}
                  </div>
                  {hasVerifiedSup && selfReportedSup > verifiedSupHrs && (
                    <div style={s.pillSub}>{selfReportedSup.toLocaleString()} supervisory hrs self-reported</div>
                  )}
                </div>
              </div>
            </div>

            {/* ═══ SECTION 2 — Progress toward next goal ═══ */}
            {nextLevel ? (
              <div style={s.goalCard}>
                <div style={s.goalLabel}>Next Goal</div>
                <div style={s.goalName}>{nextLevel.label}</div>
                <div style={s.progressTrack}>
                  <div style={{
                    ...s.progressFill,
                    width: `${animatedPct}%`,
                    transition: 'width 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  }} />
                </div>
                <div style={s.goalBarText}>
                  {hours.toLocaleString()} / {nextLevel.min.toLocaleString()} MEC hrs
                  {" — "}
                  <span style={{ color: '#d97706' }}>{(nextLevel.min - hours).toLocaleString()} hrs remaining</span>
                </div>
              </div>
            ) : isTrulyMaxed ? (
              <div style={s.goalCard}>
                <div style={s.goalName}>{"🏆"} Maximum level reached</div>
                <div style={s.goalBarText}>You've met every UXOHire career milestone.</div>
              </div>
            ) : (
              <div style={s.goalCard}>
                <div style={s.goalLabel}>Next Goal</div>
                <div style={s.goalName}>{nearestSenior.name}</div>
                {!nearestSenior.supMet ? (
                  <>
                    <div style={s.progressTrack}>
                      <div style={{
                        ...s.progressFill,
                        width: `${animatedPct}%`,
                        transition: 'width 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      }} />
                    </div>
                    <div style={s.goalBarText}>
                      {effectiveSup.toLocaleString()} / {nearestSenior.sup.toLocaleString()} supervisory hrs
                      {" — "}
                      <span style={{ color: '#d97706' }}>
                        {(nearestSenior.sup - effectiveSup).toLocaleString()} hrs remaining
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={s.goalBarText}>Supervisory hours met — finish the requirements below.</div>
                )}

                {roleChecklist(nearestSenior)}

                <button style={s.linkToggle} onClick={() => setShowAllSeniorRoles(v => !v)}>
                  {showAllSeniorRoles ? "Hide other senior roles ▲" : "View all senior roles ▼"}
                </button>
                {showAllSeniorRoles && (
                  <div>
                    {seniorWithStatus.filter(r => r.name !== nearestSenior.name).map(r => (
                      <div key={r.name} style={s.otherRole}>
                        <div style={s.otherRoleName}>{r.name}</div>
                        {roleChecklist(r)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ═══ SECTION 3 — Level roadmap (collapsed by default) ═══ */}
            <div style={s.roadmapCard}>
              <button style={s.roadmapHeader} onClick={() => setShowRoadmap(v => !v)}>
                <span style={s.roadmapTitle}>Level Requirements</span>
                <span style={s.roadmapToggle}>{showRoadmap ? "Hide ▲" : "Show ▼"}</span>
              </button>
              {!showRoadmap && (
                <div style={s.roadmapSummary}>
                  {LEVELS.length} levels {"·"} Currently: {currentLevel.label}
                </div>
              )}
              {showRoadmap && (
                <div style={s.roadmapRows}>
                  {(() => {
                    const qualifies = (lvl) => {
                      if (lvl.hoursBased) return hours >= lvl.min;
                      const meetsMEC = hours >= lvl.min;
                      const meetsSup = lvl.supervisoryMin ? supHrsGlobal >= lvl.supervisoryMin : true;
                      if (lvl.label === "UXO Quality Control") return meetsMEC && meetsSup && qualityVerified;
                      if (lvl.label === "UXO Safety Officer / UXOSO") return meetsMEC && meetsSup && hasOsha10;
                      if (lvl.label.startsWith("SUXOS")) return meetsMEC && meetsSup && hasOsha10 && qualityVerified;
                      return false;
                    };
                    let highestIdx = -1;
                    LEVELS.forEach((lvl, i) => { if (qualifies(lvl)) highestIdx = i; });
                    return LEVELS.map((lvl, i) => {
                      const isCurrent = i === highestIdx;
                      const isAchieved = qualifies(lvl);
                      const supHrs = profile.supervisory_hours || 0;
                      const meetsSuper = lvl.supervisoryMin ? supHrs >= lvl.supervisoryMin : true;
                      const meetsMEC = hours >= lvl.min;
                      return (
                        <div key={lvl.label} style={{ ...s.levelRow, ...(isCurrent ? s.levelRowActive : {}) }}>
                          <div style={s.levelRowLeft}>
                            <span style={{ ...s.levelRowDot, background: isAchieved ? "#4ade80" : "#333" }}>
                              {isAchieved ? "✓" : i + 1}
                            </span>
                            <span style={{ color: isCurrent ? "#d97706" : isAchieved ? "#e8e4dc" : "#555",
                                           fontWeight: isCurrent ? "bold" : "normal" }}>
                              {lvl.label}
                            </span>
                            {isCurrent && <span style={s.currentTag}>Current</span>}
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ color: "#7a7570", fontSize: 13 }}>
                              {lvl.note ? lvl.note
                                : lvl.max != null
                                  ? `${lvl.min.toLocaleString()} – ${lvl.max.toLocaleString()} hrs`
                                  : `${lvl.min.toLocaleString()}+ hrs`}
                            </span>
                            {lvl.supervisoryMin && meetsMEC && !meetsSuper && (
                              <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>
                                {supHrs.toLocaleString()} / {lvl.supervisoryMin.toLocaleString()} supervisory hrs
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            {/* UXO Cert summary */}
            <div style={s.certSummaryCard}>
              <div style={s.certSummaryTitle}>Your Active UXO Certifications</div>
              {(profile.dod_certs || []).length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                  {(profile.dod_certs || []).map(c => (
                    <span key={c} style={s.dodChip}>{c}</span>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#555", fontSize: 13, marginTop: 8 }}>
                  No UXO certifications on file. Add them via your profile.
                </p>
              )}
            </div>

            {/* Quality Credential status */}
            {profile.quality_credential && profile.quality_credential !== 'None' && (
              <div style={{
                background: '#111316', border: '1px solid #1e2022', borderRadius: 10,
                padding: '16px 20px', marginTop: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 'bold', color: '#e8e4dc' }}>Quality Credential: {profile.quality_credential}</span>
                  {profile.quality_credential_status === 'verified' && (
                    <span style={{
                      background: '#1a1408', color: '#d97706', fontSize: 11,
                      padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                      border: '1px solid #d9770633',
                    }}>{"✓"} Documented</span>
                  )}
                  {profile.quality_credential_status === 'pending' && (
                    <span style={{
                      background: '#2a1e08', color: '#fbbf24', fontSize: 11,
                      padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                      border: '1px solid #fbbf2433',
                    }}>Pending Review</span>
                  )}
                </div>
              </div>
            )}

          </div>
          );
        })()}

        {/* ═══════════════════════════════════════════════════
            TAB — RESOURCE DIRECTORY
        ═══════════════════════════════════════════════════ */}
        {SHOW_RESOURCES && activeSection === "resources" && (() => {
          const q = resourceSearch.trim().toLowerCase();
          const matches = (r) => !q
            || (r.title || '').toLowerCase().includes(q)
            || (r.description || '').toLowerCase().includes(q);
          const visible = resources.filter(matches);
          const featured = visible.filter(r => r.is_featured);

          const renderCard = (r, showBadge = true) => {
            const cat = RESOURCE_CATEGORIES.find(c => c.id === r.category);
            const type = RESOURCE_TYPES.find(t => t.id === r.resource_type);
            const isOpen = openArticle === r.id;
            const handleAction = () => {
              if (r.resource_type === 'article') {
                setOpenArticle(isOpen ? null : r.id);
              } else if (r.resource_type === 'pdf' && r.file_path) {
                const { data } = supabase.storage.from('resources').getPublicUrl(r.file_path);
                if (data?.publicUrl) window.open(data.publicUrl, '_blank', 'noopener,noreferrer');
              } else if (r.url) {
                window.open(r.url, '_blank', 'noopener,noreferrer');
              }
            };
            return (
              <div key={r.id} style={s.resourceCard}>
                <div style={s.resourceCardTop}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.resourceTitle}>{r.title}</div>
                    {r.description && <div style={s.resourceDesc}>{r.description}</div>}
                  </div>
                  {cat && showBadge && (
                    <span style={{ ...s.resourceBadge, background: cat.bg, color: cat.color, border: `1px solid ${cat.border}` }}>
                      {cat.label}
                    </span>
                  )}
                </div>
                <div style={s.resourceCardBottom}>
                  <span style={s.resourceType}>
                    {type?.icon} {type?.label}
                  </span>
                  <button style={s.resourceActionBtn} onClick={handleAction}>
                    {r.resource_type === 'article'
                      ? (isOpen ? 'Close' : 'Read')
                      : type?.action}
                  </button>
                </div>
                {r.resource_type === 'article' && isOpen && (
                  <div style={s.articleBody}>
                    <ArticleBody text={r.content} />
                  </div>
                )}
              </div>
            );
          };

          return (
            <div>
              <div style={s.sectionHeader}>
                <h2 style={s.sectionTitle}>Resources</h2>
                <span style={s.sectionSub}>Regulations, training, reference docs & industry guides</span>
              </div>

              <input
                style={s.resourceSearch}
                placeholder="Search resources by title or description..."
                value={resourceSearch}
                onChange={e => setResourceSearch(e.target.value)}
              />

              {resourcesLoading && (
                <div style={{ color: '#7a7570', textAlign: 'center', padding: 40 }}>Loading resources...</div>
              )}

              {!resourcesLoading && visible.length === 0 && (
                <div style={s.emptyCard}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>{"📚"}</div>
                  <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 6 }}>
                    {q ? 'No matching resources' : 'No resources yet'}
                  </div>
                  <div style={{ color: '#7a7570', fontSize: 14 }}>
                    {q ? 'Try a different search term.' : 'Check back soon — resources are on the way.'}
                  </div>
                </div>
              )}

              {!resourcesLoading && visible.length > 0 && (
                <>
                  {featured.length > 0 && (
                    <div style={{ marginBottom: 28 }}>
                      <div style={s.resourceGroupTitle}>{"⭐"} Essential Resources</div>
                      <div style={s.resourceGrid}>
                        {featured.map(r => renderCard(r, true))}
                      </div>
                    </div>
                  )}

                  {RESOURCE_CATEGORIES.map(cat => {
                    const items = visible.filter(r => r.category === cat.id && !r.is_featured);
                    if (items.length === 0) return null;
                    return (
                      <div key={cat.id} style={{ marginBottom: 28 }}>
                        <div style={{ ...s.resourceGroupTitle, color: cat.color }}>{cat.label}</div>
                        <div style={s.resourceGrid}>
                          {items.map(r => renderCard(r, false))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })()}

        {/* ═══════════════════════════════════════════════════
            TAB 4 — NOTIFICATIONS
        ═══════════════════════════════════════════════════ */}
        {activeSection === "notifs" && (
          <div>
            <div style={s.sectionHeader}>
              <h2 style={s.sectionTitle}>Notifications</h2>
              <span style={s.sectionSub}>Certifications expiring within 60 days</span>
            </div>

            {/* Server-side notifications (dismissable) */}
            {notifications.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                {notifications.map(n => {
                  const days = daysUntil(n.expiry_date);
                  const urgent = days !== null && days <= 30;
                  const color = urgent ? "#f87171" : "#fbbf24";
                  const bg = urgent ? "#2a0a0a" : "#2a1e08";
                  return (
                    <div key={n.id} style={{
                      background: bg, border: `1px solid ${color}`,
                      borderRadius: 10, padding: '16px 20px', marginBottom: 10,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      gap: 12
                    }}>
                      <div>
                        <div style={{ color, fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>
                          {urgent ? "\uD83D\uDEA8" : "\u26A0\uFE0F"} {CERT_LABELS[n.cert_type] || n.cert_type}
                        </div>
                        <div style={{ color: '#9a9490', fontSize: 13 }}>
                          {days !== null && days < 0
                            ? `Expired ${Math.abs(days)} days ago`
                            : days !== null
                              ? `Expires in ${days} days`
                              : 'Expiring soon'
                          }
                          {" \u2014 "}{n.expiry_date}
                        </div>
                      </div>
                      <button
                        onClick={() => dismissNotification(n.id)}
                        disabled={dismissing === n.id}
                        style={{
                          background: 'none', border: `1px solid ${color}33`,
                          color, cursor: 'pointer', padding: '6px 12px',
                          borderRadius: 6, fontSize: 12, fontFamily: 'inherit',
                          flexShrink: 0
                        }}
                      >
                        {dismissing === n.id ? "\u2026" : "Dismiss"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Client-side cert expiry alerts */}
            {certAlerts.length > 0 && (
              <div>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6a6660', marginBottom: 12 }}>
                  Upcoming Expirations
                </div>
                {certAlerts.map((alert, i) => {
                  const urgent = alert.days <= 30;
                  const expired = alert.days < 0;
                  const color = expired || urgent ? "#f87171" : "#fbbf24";
                  const bg = expired || urgent ? "#2a0a0a" : "#2a1e08";
                  return (
                    <div key={i} style={{
                      background: bg, border: `1px solid ${color}33`,
                      borderRadius: 10, padding: '14px 18px', marginBottom: 8,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      flexWrap: 'wrap', gap: 8
                    }}>
                      <div>
                        <div style={{ color: '#e8e4dc', fontWeight: 'bold', fontSize: 14 }}>{alert.label}</div>
                        <div style={{ color: '#7a7570', fontSize: 12, marginTop: 2 }}>
                          {expired ? 'Expired' : 'Expires'}: {new Date(alert.expiryDate).toLocaleDateString()}
                        </div>
                      </div>
                      <span style={{
                        background: `${color}22`, color, fontSize: 13, fontWeight: 'bold',
                        padding: '4px 12px', borderRadius: 12, border: `1px solid ${color}33`
                      }}>
                        {expired ? `Expired ${Math.abs(alert.days)}d ago` : `${alert.days} days left`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {notifications.length === 0 && certAlerts.length === 0 && (
              <div style={s.emptyState}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{"\u2705"}</div>
                <p style={{ color: '#e8e4dc', fontSize: 15 }}>All clear!</p>
                <p style={{ color: "#555", fontSize: 13 }}>
                  No certifications expiring within 60 days. You're in good standing.
                </p>
              </div>
            )}
          </div>
        )}

      </div>{/* /body */}
    </div>
  );
}

// ─── CertCard sub-component ──────────────────────────────────────────────────
function CertCard({ label, held, issueDate, expiryDate, lifetime, lifetimeText, renewalNote, requiredFor }) {
  let st;
  if (!held)         st = { status: "missing", days: null };
  else if (lifetime) st = { status: "lifetime", days: null };
  else               st = certStatus(expiryDate);

  // Missing cert that a selected senior role requires → amber/warning highlight
  const isRequiredMissing = !held && Array.isArray(requiredFor) && requiredFor.length > 0;
  const color = isRequiredMissing ? "#d97706" : STATUS_COLOR[st.status];
  const bg    = isRequiredMissing ? "#1f1608" : STATUS_BG[st.status];

  return (
    <div style={{ ...s.certCard, borderColor: `${color}33`, background: bg }}>
      <div style={s.certCardTop}>
        <span style={{ ...s.certCardLabel, color: held ? "#e8e4dc" : isRequiredMissing ? "#e8c98a" : "#555" }}>{label}</span>
        <StatusBadge status={st.status} days={st.days} amber={isRequiredMissing} />
      </div>
      {held && (
        <div style={s.certCardMeta}>
          {issueDate && <span>Issued: {new Date(issueDate).toLocaleDateString()}</span>}
          {expiryDate && !lifetime && (
            <span style={{ color: color }}>
              Expires: {new Date(expiryDate).toLocaleDateString()}
            </span>
          )}
          {lifetime && lifetimeText && (
            <span style={{ color: "#4ade80" }}>{lifetimeText}</span>
          )}
          {lifetime && !lifetimeText && !expiryDate && <span style={{ color: "#4ade80" }}>No expiration</span>}
          {renewalNote && <span style={{ color: "#555" }}>{renewalNote}</span>}
        </div>
      )}
      {!held && !isRequiredMissing && (
        <div style={{ color: "#555", fontSize: 12, marginTop: 4 }}>Not on file</div>
      )}
      {isRequiredMissing && (
        <div style={{ color: "#d97706", fontSize: 12, marginTop: 4, fontWeight: "bold" }}>
          Required for {requiredFor.join(", ")}
        </div>
      )}
    </div>
  );
}

// ─── MatchBadge sub-component ─────────────────────────────────────────────────
function MatchBadge({ pct }) {
  const color = pct >= 90 ? "#4ade80" : pct >= 60 ? "#fbbf24" : "#f87171";
  return (
    <div style={{ textAlign: "center", flexShrink: 0, marginLeft: 12 }}>
      <svg width={56} height={56} viewBox="0 0 56 56">
        <circle cx={28} cy={28} r={24} fill="none" stroke="#1e2022" strokeWidth={5} />
        <circle
          cx={28} cy={28} r={24} fill="none"
          stroke={color} strokeWidth={5}
          strokeDasharray={`${(pct / 100) * 150.8} 150.8`}
          strokeLinecap="round"
          transform="rotate(-90 28 28)"
        />
        <text x={28} y={33} textAnchor="middle" fill={color}
              fontSize={13} fontWeight="bold" fontFamily="Georgia, serif">
          {pct}%
        </text>
      </svg>
    </div>
  );
}

// ─── DocumentsTab sub-component ─────────────────────────────────────────────
const BASE_DOC_TYPES = [
  { key: 'resume',        label: 'Resume',                       expiresYears: null },
  { key: 'hazwoper_8hr',  label: 'HAZWOPER 8-Hour Refresher Cert', expiresYears: 1 },
  { key: 'hazwoper_40hr', label: 'HAZWOPER 40-Hour Cert',          expiresYears: null },
  { key: 'uxo_eod_cert',  label: 'UXO/EOD Certification',        expiresYears: null },
  { key: 'physical',      label: 'Physical Exam Cert',            expiresYears: 1 },
  { key: 'first_aid_cpr', label: 'First Aid/CPR Cert',            expiresYears: 2 },
  { key: 'logbook',       label: 'Signed Logbook',               expiresYears: null, pdfOnly: true,
    description: 'Upload your signed logbook showing your field hours. Must be signed by a current or former supervisor.' },
  { key: 'quality_cert',  label: 'Quality Credential Certificate', expiresYears: null, conditional: true,
    description: 'Upload your quality credential certificate (ISO 9001, ASQ, USACE/NAVFAC, or company QC training).' },
  { key: 'osha_10_construction', label: '10-Hour OSHA Construction Certificate', expiresYears: null, conditional: true,
    description: 'Required for UXOSO and SUXOS positions. Upload your DOL Construction Safety Course Completion Card.' },
];

const MAX_DOC_SIZE = 10 * 1024 * 1024;
const DOC_ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const DOC_PDF_ONLY = ['application/pdf'];

function docExpiryBadge(expiresAt) {
  if (!expiresAt) return { label: 'No Expiration', color: '#4ade80', bg: '#0a2a1a' };
  const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0)   return { label: 'Expired',       color: '#f87171', bg: '#2a0a0a' };
  if (days <= 60) return { label: `${days}d left`,  color: '#fbbf24', bg: '#2a1e08' };
  return { label: 'Active', color: '#4ade80', bg: '#0a2a1a' };
}

function DocumentsTab({ profile, user }) {
  const navigate = useNavigate();
  const hasQualityCred = profile?.quality_credential && profile.quality_credential !== 'None';
  const hasOsha10 = !!profile?.osha_10_construction;
  const DOC_TYPES = BASE_DOC_TYPES.filter(d => !d.conditional || (d.key === 'quality_cert' && hasQualityCred) || (d.key === 'osha_10_construction' && hasOsha10));
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [datePrompt, setDatePrompt] = useState(null); // { docType, saving }
  const [datePickerValue, setDatePickerValue] = useState('');
  const [logbookMsg, setLogbookMsg] = useState('');
  const [docConsent, setDocConsent] = useState(null); // null=loading, true/false
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);

  // Fetch consent status
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await supabase.from('user_accounts')
        .select('doc_consent_accepted')
        .eq('user_id', user.id)
        .single();
      if (error) console.error('Failed to fetch doc consent:', error);
      setDocConsent(data?.doc_consent_accepted === true);
    })();
  }, [user]);

  const handleAcceptConsent = async () => {
    setConsentSaving(true);
    const { error } = await supabase.from('user_accounts').update({
      doc_consent_accepted: true,
      doc_consent_accepted_at: new Date().toISOString(),
    }).eq('user_id', user.id);
    if (error) console.error('Failed to save doc consent:', error);
    setDocConsent(true);
    setConsentSaving(false);
  };

  const fetchDocs = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    const { data } = await supabase
      .from('tech_documents')
      .select('*')
      .eq('tech_profile_id', profile.id)
      .order('uploaded_at', { ascending: false });
    setDocs(data || []);
    setLoading(false);
  }, [profile]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const getDoc = (type) => docs.find(d => d.document_type === type);

  const handleUpload = async (e, docType) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const docDef = DOC_TYPES.find(d => d.key === docType);
    const accepted = docDef?.pdfOnly ? DOC_PDF_ONLY : DOC_ACCEPTED_TYPES;
    if (!accepted.includes(file.type)) {
      setError(docDef?.pdfOnly ? 'Only PDF files are allowed for logbooks.' : 'Only PDF, JPG, and PNG files are allowed.');
      return;
    }
    if (file.size > MAX_DOC_SIZE) { setError('File must be under 10MB.'); return; }
    setError('');
    setUploading(docType);

    const storagePath = `${user.id}/docs/${docType}/${Date.now()}_${file.name}`;
    const { error: sErr } = await supabase.storage.from('uxo-uploads').upload(storagePath, file, { upsert: true });
    if (sErr) { setError('Upload failed. Please try again.'); setUploading(null); return; }

    // Compute expiration from profile cert dates (not upload date)
    let expiresAt = null;
    const addYrs = (dateStr, years) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      d.setFullYear(d.getFullYear() + years);
      return d.toISOString().split('T')[0];
    };
    if (docType === 'hazwoper_8hr') expiresAt = addYrs(profile.hazwoper_8_date, 1);
    else if (docType === 'physical') expiresAt = addYrs(profile.physical_date, 1);
    else if (docType === 'first_aid_cpr') expiresAt = addYrs(profile.first_aid_cpr_date, 2);

    // Remove old file if replacing
    const existing = getDoc(docType);
    if (existing) {
      await supabase.storage.from('uxo-uploads').remove([existing.file_path]);
    }

    const { error: dbErr } = await supabase.from('tech_documents')
      .upsert({
        tech_profile_id: profile.id,
        user_id: user.id,
        document_type: docType,
        file_path: storagePath,
        file_name: file.name,
        file_size: file.size,
        uploaded_at: new Date().toISOString(),
        expires_at: expiresAt,
      }, { onConflict: 'tech_profile_id,document_type' });

    if (dbErr) { setError('Failed to save document.'); setUploading(null); return; }

    // Logbook upload: set verification status to pending and notify admin
    if (docType === 'logbook') {
      await supabase.from('tech_profiles')
        .update({ logbook_verification_status: 'pending' })
        .eq('id', profile.id);
      // Notify admin of new logbook submission
      supabase.functions.invoke('send-logbook-notification', {
        body: { type: 'new_submission', tech_profile_id: profile.id },
      }).catch(() => {});
      setLogbookMsg('Logbook uploaded successfully. Please allow up to 5 business days for review.');
      setTimeout(() => setLogbookMsg(''), 8000);
    }

    // Quality cert upload: set verification status to pending and notify admin
    if (docType === 'quality_cert') {
      await supabase.from('tech_profiles')
        .update({ quality_credential_status: 'pending' })
        .eq('id', profile.id);
      supabase.functions.invoke('send-logbook-notification', {
        body: { type: 'quality_cert_submission', tech_profile_id: profile.id },
      }).catch(() => {});
      setLogbookMsg('Quality credential uploaded successfully. Please allow up to 5 business days for review.');
      setTimeout(() => setLogbookMsg(''), 8000);
    }

    setUploading(null);
    await fetchDocs();

    // Prompt to update issue date for expiring cert types (only on replacement)
    if (existing && ['hazwoper_8hr', 'physical', 'first_aid_cpr'].includes(docType)) {
      setDatePrompt({ docType, saving: false });
      setDatePickerValue('');
    }
  };

  const handleDelete = async (doc) => {
    setDeleting(doc.id);
    await supabase.storage.from('uxo-uploads').remove([doc.file_path]);
    await supabase.from('tech_documents').delete().eq('id', doc.id);
    setDeleting(null);
    fetchDocs();
  };

  const CERT_DATE_MAP = {
    hazwoper_8hr:  { profileCol: 'hazwoper_8_date', label: 'HAZWOPER 8-Hour', years: 1 },
    physical:      { profileCol: 'physical_date',   label: 'Physical',      years: 1 },
    first_aid_cpr: { profileCol: 'first_aid_cpr_date', label: 'First Aid/CPR', years: 2 },
  };

  const handleDateUpdate = async () => {
    if (!datePrompt || !datePickerValue) return;
    const { docType } = datePrompt;
    const mapping = CERT_DATE_MAP[docType];
    if (!mapping) return;

    setDatePrompt(prev => ({ ...prev, saving: true }));

    const { error: profErr } = await supabase.from('tech_profiles')
      .update({ [mapping.profileCol]: datePickerValue })
      .eq('id', profile.id);
    if (profErr) { setDatePrompt(prev => ({ ...prev, saving: false })); return; }

    // Recalculate expires_at
    const expD = new Date(datePickerValue);
    expD.setFullYear(expD.getFullYear() + mapping.years);
    const newExpiry = expD.toISOString().split('T')[0];

    const doc = docs.find(d => d.document_type === docType);
    if (doc) {
      await supabase.from('tech_documents')
        .update({ expires_at: newExpiry })
        .eq('id', doc.id);
    }

    setDatePrompt(null);
    setDatePickerValue('');
    fetchDocs();
  };

  if (loading || docConsent === null) return <p style={{ color: '#7a7570' }}>Loading documents...</p>;

  // Consent gate — must accept before uploading
  if (!docConsent) {
    return (
      <div>
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>My Documents</h2>
          <span style={s.sectionSub}>Manage your uploaded documents</span>
        </div>
        <div style={{
          background: '#111316', border: '1px solid #d9770644', borderRadius: 10,
          padding: '24px 24px 20px', maxWidth: 560,
        }}>
          <div style={{ fontSize: 17, fontWeight: 'bold', color: '#e8e4dc', marginBottom: 12 }}>
            Before you upload your documents
          </div>
          <div style={{ fontSize: 13, color: '#b0aca6', lineHeight: 1.7, marginBottom: 16 }}>
            Documents you upload to UXOHire — including your resume, certifications, physical exam records, and logbook — may be viewed and downloaded by employers who unlock your profile.
          </div>
          <div style={{ fontSize: 13, color: '#b0aca6', lineHeight: 1.7, marginBottom: 16 }}>
            By uploading documents you confirm:
          </div>
          <ul style={{ fontSize: 13, color: '#b0aca6', lineHeight: 1.8, margin: '0 0 20px 0', paddingLeft: 20 }}>
            <li>You have the right to share these documents</li>
            <li>You consent to employers viewing and downloading them after unlocking your profile</li>
            <li>You understand UXOHire stores these documents securely on your behalf</li>
          </ul>
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
            fontSize: 13, color: '#e8e4dc', lineHeight: 1.5, marginBottom: 16,
          }}>
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={e => setConsentChecked(e.target.checked)}
              style={{ accentColor: '#d97706', marginTop: 3, flexShrink: 0 }}
            />
            I have read and understand that my uploaded documents can be viewed and downloaded by employers who unlock my profile. I consent to sharing these documents.
          </label>
          <button
            onClick={handleAcceptConsent}
            disabled={!consentChecked || consentSaving}
            style={{
              background: consentChecked ? '#d97706' : '#333',
              border: 'none', color: consentChecked ? '#0d0f10' : '#666',
              padding: '10px 28px', borderRadius: 8, fontSize: 14,
              fontWeight: 'bold', cursor: consentChecked ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
            }}
          >
            {consentSaving ? 'Saving...' : 'I Agree'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={s.sectionHeader}>
        <h2 style={s.sectionTitle}>My Documents</h2>
        <span style={s.sectionSub}>Manage your uploaded documents</span>
      </div>

      {error && (
        <div style={{ background: '#2a0a0a', border: '1px solid #8B0000', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, color: '#ff6b6b', marginBottom: 16 }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', float: 'right', fontSize: 14 }}>{"\u2715"}</button>
        </div>
      )}

      {logbookMsg && (
        <div style={{ background: '#0a2a1a', border: '1px solid #4ade8044', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, color: '#4ade80', marginBottom: 16 }}>
          {logbookMsg}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {DOC_TYPES.map((docDef, idx) => {
          const doc = getDoc(docDef.key);
          const badge = doc ? docExpiryBadge(doc.expires_at) : null;
          const isUploading = uploading === docDef.key;
          const isDeleting = doc && deleting === doc.id;
          const inputId = `dash-doc-${docDef.key}`;

          return (
            <div key={docDef.key}>
            {docDef.key === 'logbook' && (
              <div style={{ marginBottom: 12, marginTop: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 'bold', color: '#e8e4dc', marginBottom: 4 }}>Hour Documentation</div>
                <div style={{ fontSize: 13, color: '#7a7570', lineHeight: 1.5 }}>
                  Get your field hours documented by UXOHire to earn an Hours Documented badge on your profile
                </div>
                {/* Mismatch prevention — techs routinely submit a profile figure above
                    what the logbook supports, which is the single most common reason a
                    submission comes back for correction. Say so before they upload. */}
                <div style={{ fontSize: 12, color: '#7a7570', lineHeight: 1.5, marginTop: 6 }}>
                  We document hours based on your logbook. If your logbook shows more hours than your
                  profile, we'll document the lower number. Make sure your profile hours match your
                  logbook before submitting.
                </div>
              </div>
            )}
            <div style={{
              background: '#111316', border: `1px solid ${doc ? (badge.color + '33') : '#1e2022'}`,
              borderRadius: 10, padding: '16px 20px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 'bold', color: '#e8e4dc' }}>{docDef.label}</span>
                    {doc && docDef.key === 'logbook' && profile.logbook_verification_status === 'verified' && (
                      <span style={{
                        background: '#1a1408', color: '#d97706', fontSize: 11,
                        padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                        border: '1px solid #d9770633',
                      }}>{"\u2713"} {profile.logbook_verified_hours?.toLocaleString()} hrs documented</span>
                    )}
                    {doc && docDef.key === 'logbook' && profile.logbook_verification_status === 'pending' && (
                      <span style={{
                        background: '#2a1e08', color: '#fbbf24', fontSize: 11,
                        padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                        border: '1px solid #fbbf2433',
                      }}>{"\u23F3"} Pending Review</span>
                    )}
                    {doc && docDef.key === 'logbook' && profile.logbook_verification_status === 'returned' && (
                      <span style={{
                        background: '#2a1e08', color: '#fbbf24', fontSize: 11,
                        padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                        border: '1px solid #fbbf2433',
                      }}>{"\u21A9"} Action needed \u2014 see note</span>
                    )}
                    {doc && docDef.key === 'quality_cert' && profile.quality_credential_status === 'verified' && (
                      <span style={{
                        background: '#1a1408', color: '#d97706', fontSize: 11,
                        padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                        border: '1px solid #d9770633',
                      }}>{"\u2713"} Documented</span>
                    )}
                    {doc && docDef.key === 'quality_cert' && profile.quality_credential_status === 'pending' && (
                      <span style={{
                        background: '#2a1e08', color: '#fbbf24', fontSize: 11,
                        padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                        border: '1px solid #fbbf2433',
                      }}>{"\u23F3"} Pending Review</span>
                    )}
                    {doc && docDef.key !== 'logbook' && docDef.key !== 'quality_cert' && badge && (
                      <span style={{
                        background: badge.bg, color: badge.color, fontSize: 11,
                        padding: '2px 8px', borderRadius: 10, fontWeight: 'bold',
                        border: `1px solid ${badge.color}33`,
                      }}>{"\u25CF"} {badge.label}</span>
                    )}
                  </div>

                  {doc && (
                    <div style={{ fontSize: 12, color: '#7a7570', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span>{doc.file_name}</span>
                      <span>
                        Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                        {doc.expires_at && ` \u00B7 Expires: ${new Date(doc.expires_at).toLocaleDateString()}`}
                      </span>
                      {docDef.key === 'logbook' && profile.logbook_verification_status === 'pending' && (
                        <span style={{ color: '#fbbf24', fontSize: 11, marginTop: 2 }}>
                          Please allow up to 5 business days for logbook review.
                        </span>
                      )}
                      {/* Returned for correction. The reviewer's note is the whole
                          point of this state, so it is shown inline. Two actions,
                          because the fix is as often a profile-hours edit as it is
                          a bad logbook — re-upload alone strands those techs. */}
                      {docDef.key === 'logbook' && profile.logbook_verification_status === 'returned' && (
                        <div style={{
                          marginTop: 6, padding: '8px 10px', borderRadius: 6,
                          background: '#2a1e08', border: '1px solid #fbbf2433',
                        }}>
                          <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 'bold', marginBottom: 2 }}>
                            Action needed before we can document these hours
                          </div>
                          {profile.logbook_verification_notes && (
                            <div style={{ color: '#b0aca6', fontSize: 11, lineHeight: 1.5 }}>
                              {profile.logbook_verification_notes}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                            <label htmlFor={inputId} style={{
                              background: '#d97706', border: 'none', color: '#0d0f10',
                              padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                              fontWeight: 'bold', fontFamily: 'inherit', whiteSpace: 'nowrap',
                            }}>
                              {isUploading ? 'Uploading...' : 'Upload corrected logbook'}
                            </label>
                            <button onClick={() => navigate('/create-profile')} style={{
                              background: 'none', border: '1px solid #d9770666', color: '#d97706',
                              padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                              fontFamily: 'inherit', whiteSpace: 'nowrap',
                            }}>
                              Edit profile hours
                            </button>
                          </div>
                          <div style={{ color: '#7a7570', fontSize: 11, marginTop: 8 }}>
                            Questions about this?{' '}
                            <button onClick={() => navigate('/support')} style={{
                              background: 'none', border: 'none', padding: 0, color: '#d97706',
                              textDecoration: 'underline', cursor: 'pointer', fontSize: 11,
                              fontFamily: 'inherit',
                            }}>Contact support</button>
                          </div>
                        </div>
                      )}
                      {docDef.key === 'quality_cert' && profile.quality_credential_status === 'pending' && (
                        <span style={{ color: '#fbbf24', fontSize: 11, marginTop: 2 }}>
                          Please allow up to 5 business days for quality credential review.
                        </span>
                      )}
                    </div>
                  )}

                  {!doc && (
                    <div style={{ fontSize: 12, color: '#555' }}>
                      {docDef.description || 'Not uploaded'}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                  {doc && (
                    <>
                      <label htmlFor={inputId} style={{
                        background: 'none', border: '1px solid #d9770666', color: '#d97706',
                        padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                        fontFamily: 'inherit', whiteSpace: 'nowrap',
                      }}>
                        {isUploading ? 'Uploading...' : 'Re-upload'}
                      </label>
                      <button onClick={() => handleDelete(doc)} disabled={isDeleting} style={{
                        background: 'none', border: '1px solid #f8717133', color: '#f87171',
                        padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                        fontFamily: 'inherit', whiteSpace: 'nowrap',
                      }}>
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </>
                  )}
                  {!doc && (
                    <label htmlFor={inputId} style={{
                      background: '#d97706', border: 'none', color: '#0d0f10',
                      padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                      fontWeight: 'bold', fontFamily: 'inherit', whiteSpace: 'nowrap',
                    }}>
                      {isUploading ? 'Uploading...' : 'Upload'}
                    </label>
                  )}
                  <input id={inputId} type="file" accept={docDef.pdfOnly ? '.pdf' : '.pdf,.jpg,.jpeg,.png'} style={{ display: 'none' }}
                    onChange={e => handleUpload(e, docDef.key)} disabled={isUploading} />
                </div>
              </div>

              {datePrompt?.docType === docDef.key && (
                <div style={{
                  background: '#1a1408', border: '1px solid #d9770644', borderRadius: 6,
                  padding: '10px 14px', marginTop: 10,
                }}>
                  <div style={{ fontSize: 12, color: '#d97706', marginBottom: 8 }}>
                    Document updated! Would you like to update your {CERT_DATE_MAP[docDef.key]?.label} issue date?
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <input type="date" value={datePickerValue} onChange={e => setDatePickerValue(e.target.value)}
                      style={{
                        background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 4,
                        color: '#e8e4dc', padding: '4px 8px', fontSize: 12, fontFamily: 'inherit',
                      }} />
                    <button onClick={handleDateUpdate} disabled={!datePickerValue || datePrompt.saving}
                      style={{
                        background: datePickerValue ? '#d97706' : '#555',
                        color: datePickerValue ? '#0d0f10' : '#999',
                        border: 'none', padding: '4px 12px', borderRadius: 4, fontSize: 11,
                        fontWeight: 'bold', cursor: datePickerValue ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit',
                      }}>
                      {datePrompt.saving ? 'Saving...' : 'Update Date'}
                    </button>
                    <button onClick={() => { setDatePrompt(null); setDatePickerValue(''); }}
                      style={{
                        background: 'none', border: 'none', color: '#7a7570', fontSize: 11,
                        cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline',
                      }}>
                      Skip for now
                    </button>
                  </div>
                </div>
              )}
            </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: '#555', marginTop: 12 }}>
        Accepted formats: PDF, JPG, PNG. Max 10MB per file. Documents are visible to employers who unlock your profile.
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  root:          { fontFamily: "'Georgia','Times New Roman',serif", background: "#0d0f10",
                   color: "#e8e4dc", minHeight: "100vh" },
  header:        { background: "#111316", borderBottom: "1px solid #222",
                   padding: "0 0 0 0", position: "sticky", top: 0, zIndex: 100 },
  headerInner:   { maxWidth: 900, margin: "0 auto", padding: "16px 20px",
                   display: "flex", alignItems: "center", justifyContent: "space-between",
                   flexWrap: "wrap", gap: 8 },
  headerLabel:   { fontSize: 11, color: "#6a6660", textTransform: "uppercase",
                   letterSpacing: "0.1em", marginBottom: 2 },
  headerName:    { fontSize: 20, fontWeight: "bold", letterSpacing: "-0.3px",
                   overflowWrap: "break-word", wordBreak: "break-word" },
  headerRight:   { display: "flex", alignItems: "center", gap: 12 },
  editProfileBtn:{ background: "#d97706", border: "none", color: "#0d0f10",
                   cursor: "pointer", padding: "6px 14px", fontSize: 13,
                   borderRadius: 6, fontFamily: "inherit", fontWeight: "bold" },
  backBtn:       { background: "none", border: "1px solid #333", color: "#9a9490",
                   cursor: "pointer", padding: "6px 14px", fontSize: 13,
                   borderRadius: 6, fontFamily: "inherit" },
  newOptionsBanner: { background: "#2a1e08", border: "1px solid #d97706", borderRadius: 8,
                   padding: "14px 16px", fontSize: 14, color: "#d97706",
                   display: "flex", alignItems: "center", gap: 12,
                   flexWrap: "wrap", lineHeight: 1.5 },
  newOptionsBtnUpdate: { background: "#d97706", border: "none", color: "#0d0f10",
                   cursor: "pointer", padding: "6px 14px", fontSize: 13,
                   borderRadius: 6, fontFamily: "inherit", fontWeight: "bold",
                   whiteSpace: "nowrap" },
  newOptionsBtnDismiss: { background: "none", border: "1px solid #d9770666", color: "#d97706",
                   cursor: "pointer", padding: "6px 14px", fontSize: 13,
                   borderRadius: 6, fontFamily: "inherit",
                   whiteSpace: "nowrap" },
  notifWrap:     { maxWidth: 900, margin: "12px auto 0", padding: "0 20px" },
  notifBanner:   { background: "#2a1e08", border: "1px solid #fbbf24", borderRadius: 8,
                   padding: "12px 16px", fontSize: 13, color: "#fbbf24",
                   marginBottom: 8, display: "flex", justifyContent: "space-between",
                   alignItems: "center", gap: 12, overflowWrap: "break-word",
                   wordBreak: "break-word" },
  notifBannerUrgent: { background: "#2a0a0a", border: "1px solid #f87171", color: "#f87171" },
  notifDismiss:  { background: "none", border: "none", color: "inherit",
                   cursor: "pointer", fontSize: 16, padding: "0 4px", lineHeight: 1 },
  tabBar:        { maxWidth: 900, margin: "0 auto", padding: "12px 20px 0",
                   display: "flex", gap: 4, borderBottom: "1px solid #1e2022",
                   overflowX: "auto", WebkitOverflowScrolling: "touch" },
  tab:           { background: "none", border: "none", color: "#9a9590", cursor: "pointer",
                   padding: "8px 16px 12px", fontSize: 15, fontFamily: "inherit",
                   borderBottom: "2px solid transparent", display: "flex",
                   alignItems: "center", gap: 6, transition: "color 0.15s",
                   minHeight: 44, flexShrink: 0, whiteSpace: "nowrap" },
  tabActive:     { color: "#e8e4dc", borderBottom: "2px solid #d97706", fontWeight: "bold" },
  tabIcon:       { fontSize: 16 },
  tabLabel:      { lineHeight: 1.2 },
  body:          { maxWidth: 900, margin: "0 auto", padding: "28px 20px 60px" },

  sectionHeader: { display: "flex", alignItems: "baseline", gap: 12, marginBottom: 20,
                   flexWrap: "wrap" },
  sectionTitle:  { fontSize: 22, fontWeight: "bold", margin: 0, letterSpacing: "-0.3px",
                   overflowWrap: "break-word", wordBreak: "break-word" },
  sectionSub:    { color: "#6a6660", fontSize: 13 },

  // Resource directory
  resourceSearch:   { background: "#161819", border: "1px solid #2a2c2e", color: "#e8e4dc",
                      padding: "11px 16px", borderRadius: 8, fontSize: 14, fontFamily: "inherit",
                      outline: "none", width: "100%", boxSizing: "border-box", marginBottom: 24 },
  resourceGroupTitle:{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.08em",
                      color: "#9a9590", fontWeight: "bold", marginBottom: 12 },
  resourceGrid:     { display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))",
                      gap: 14 },
  resourceCard:     { background: "#111316", border: "1px solid #1e2022", borderRadius: 10,
                      padding: "16px 18px", display: "flex", flexDirection: "column" },
  resourceCardTop:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      gap: 10, marginBottom: 12 },
  resourceTitle:    { fontSize: 16, fontWeight: "bold", lineHeight: 1.4,
                      overflowWrap: "break-word", wordBreak: "break-word" },
  resourceDesc:     { fontSize: 13, color: "#9a9590", lineHeight: 1.5, marginTop: 5,
                      overflowWrap: "break-word", wordBreak: "break-word" },
  resourceBadge:    { fontSize: 10, padding: "3px 9px", borderRadius: 12, fontWeight: "bold",
                      whiteSpace: "nowrap", flexShrink: 0 },
  resourceCardBottom:{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      gap: 10, marginTop: "auto", flexWrap: "wrap" },
  resourceType:     { fontSize: 12, color: "#7a7570" },
  resourceActionBtn:{ background: "none", border: "1px solid #d97706", color: "#d97706",
                      padding: "6px 16px", fontSize: 13, borderRadius: 6, cursor: "pointer",
                      fontFamily: "inherit", fontWeight: "bold", whiteSpace: "nowrap" },
  articleBody:      { marginTop: 14, paddingTop: 14, borderTop: "1px solid #1e2022",
                      fontSize: 14, color: "#cfcabf",
                      overflowWrap: "break-word", wordBreak: "break-word" },

  // Job match
  jobGrid:       { display: "flex", flexDirection: "column", gap: 16 },
  jobCard:       { background: "#111316", border: "1px solid #1e2022", borderRadius: 10,
                   padding: "20px 22px" },
  jobCardTop:    { display: "flex", justifyContent: "space-between",
                   alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 10 },
  jobCompany:    { fontSize: 11, color: "#7a7570", textTransform: "uppercase",
                   letterSpacing: "0.06em", marginBottom: 3,
                   overflowWrap: "break-word", wordBreak: "break-word" },
  jobTitle:      { fontSize: 18, fontWeight: "bold", lineHeight: 1.4, marginBottom: 6,
                   overflowWrap: "break-word", wordBreak: "break-word" },
  jobMeta:       { display: "flex", gap: 14, fontSize: 12, color: "#7a7570", flexWrap: "wrap",
                   lineHeight: 1.5 },
  matchSection:  { marginTop: 10 },
  matchLabel:    { fontSize: 11, color: "#6a6660", textTransform: "uppercase",
                   letterSpacing: "0.06em", marginBottom: 5 },
  certRow:       { display: "flex", flexWrap: "wrap", gap: 6 },
  certChip:      { fontSize: 11, padding: "3px 8px", borderRadius: 4 },
  certChipMet:   { background: "#0a2a1a", color: "#4ade80", border: "1px solid #4ade8033" },
  certChipMissed:{ background: "#2a0a0a", color: "#f87171", border: "1px solid #f8717133" },
  certChipPref:  { background: "#0a1a2a", color: "#60a5fa", border: "1px solid #60a5fa33" },
  certChipNeutral:{ background: "#1a1c1e", color: "#555", border: "1px solid #2a2c2e" },
  applyBtn:      { marginTop: 14, background: "none", border: "1px solid #d97706",
                   color: "#d97706", padding: "7px 16px", fontSize: 13,
                   borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                   fontWeight: "bold" },

  // Cert tracker
  certGrid:      { display: "grid",
                   gridTemplateColumns: "repeat(auto-fill, minmax(min(280px, 100%), 1fr))",
                   gap: 12 },
  certCard:      { background: "#111316", border: "1px solid #1e2022", borderRadius: 8,
                   padding: "14px 16px" },
  certCardTop:   { display: "flex", justifyContent: "space-between",
                   alignItems: "flex-start", gap: 8, marginBottom: 6, flexWrap: "wrap" },
  certCardLabel: { fontSize: 13, fontWeight: "bold", lineHeight: 1.4,
                   flex: 1, minWidth: 0, overflowWrap: "break-word", wordBreak: "break-word" },
  certCardMeta:  { display: "flex", flexDirection: "column", gap: 3, fontSize: 12,
                   color: "#7a7570", lineHeight: 1.5 },
  unsetToggle:   { width: "100%", marginTop: 18, background: "none",
                   border: "1px dashed #2a2c2e", borderRadius: 8, color: "#9a9590",
                   cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: "bold",
                   padding: "11px 16px", textAlign: "center" },

  // Career
  levelHero:     { background: "#111316", border: "1px solid #d97706", borderRadius: 12,
                   padding: "24px 28px", marginBottom: 20, overflowWrap: "break-word" },
  levelBadgeWrap:{ marginBottom: 8 },
  levelBadge:    { display: "inline-block", background: "#1a1408",
                   border: "1px solid #d97706", color: "#d97706",
                   fontSize: 16, fontWeight: "bold", padding: "4px 16px",
                   borderRadius: 20, marginBottom: 8, overflowWrap: "break-word",
                   maxWidth: "100%" },
  levelHours:    { fontSize: 24, fontWeight: "bold", letterSpacing: "-0.5px",
                   overflowWrap: "break-word" },
  levelNext:     { fontSize: 14, lineHeight: 1.6, overflowWrap: "break-word",
                   flexWrap: "wrap" },
  progressWrap:  { background: "#111316", border: "1px solid #1e2022",
                   borderRadius: 10, padding: "20px 22px", marginBottom: 20 },
  progressMeta:  { display: "flex", justifyContent: "space-between",
                   marginBottom: 10, flexWrap: "wrap", gap: 8, rowGap: 6 },
  progressTrack: { background: "#1e2022", borderRadius: 8, height: 14, overflow: "hidden" },
  progressFill:  { height: "100%", background: "linear-gradient(90deg, #d97706, #f59e0b)",
                   borderRadius: 8 },
  levelsTable:   { background: "#111316", border: "1px solid #1e2022",
                   borderRadius: 10, overflow: "hidden", marginBottom: 16 },
  levelsTitle:   { fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em",
                   color: "#6a6660", padding: "14px 18px",
                   borderBottom: "1px solid #1e2022" },
  levelRow:      { display: "flex", alignItems: "center", justifyContent: "space-between",
                   padding: "13px 18px", borderBottom: "1px solid #1a1c1e",
                   flexWrap: "wrap", gap: 6 },
  levelRowActive:{ background: "#1a1408" },
  levelRowLeft:  { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  levelRowDot:   { width: 24, height: 24, borderRadius: "50%",
                   display: "flex", alignItems: "center", justifyContent: "center",
                   fontSize: 11, fontWeight: "bold", color: "#0d0f10", flexShrink: 0 },
  currentTag:    { background: "#d97706", color: "#0d0f10", fontSize: 10,
                   fontWeight: "bold", padding: "2px 7px", borderRadius: 10,
                   letterSpacing: "0.04em" },
  certSummaryCard:{ background: "#111316", border: "1px solid #1e2022",
                    borderRadius: 10, padding: "18px 20px" },
  certSummaryTitle:{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em",
                     color: "#6a6660" },
  dodChip:       { background: "#1a1408", border: "1px solid #d97706",
                   color: "#d97706", fontSize: 12, padding: "4px 10px", borderRadius: 6 },

  // Career — redesigned tab
  careerWarn:    { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                   background: "#17120a", border: "1px solid #3a2c12", borderLeftWidth: 3,
                   borderLeftColor: "#d97706", borderRadius: 8, padding: "10px 14px",
                   fontSize: 13, color: "#e3b264", marginBottom: 16, lineHeight: 1.5 },
  careerWarnLink:{ background: "none", border: "none", color: "#d97706", cursor: "pointer",
                   fontFamily: "inherit", fontSize: 13, fontWeight: "bold",
                   textDecoration: "underline", padding: 0, whiteSpace: "nowrap" },
  careerWarnClose:{ background: "none", border: "none", color: "#8a7a55", cursor: "pointer",
                   fontFamily: "inherit", fontSize: 14, lineHeight: 1, padding: "0 2px",
                   flexShrink: 0 },
  careerPending: { background: "#17120a", border: "1px solid #3a2c12", borderRadius: 8,
                   padding: "10px 14px", fontSize: 13, color: "#fbbf24", marginBottom: 16,
                   lineHeight: 1.5 },

  statusCard:    { background: "#111316", border: "1px solid #1e2022", borderRadius: 12,
                   padding: "24px 26px", marginBottom: 16 },
  statusBadge:   { display: "inline-block", background: "#1a1408", border: "1px solid #d97706",
                   color: "#d97706", fontSize: 18, fontWeight: "bold", padding: "6px 18px",
                   borderRadius: 22, marginBottom: 18, letterSpacing: "-0.2px",
                   maxWidth: "100%", overflowWrap: "break-word" },
  pillRow:       { display: "flex", gap: 12, flexWrap: "wrap" },
  pillCol:       { flex: "1 1 220px", minWidth: 0 },
  pillAmber:     { background: "#1a1408", border: "1px solid #d97706", borderRadius: 10,
                   padding: "12px 16px", fontSize: 15, fontWeight: "bold", color: "#e8e4dc",
                   lineHeight: 1.4, overflowWrap: "break-word" },
  pillTeal:      { background: "#0c1c1a", border: "1px solid #14b8a6", borderRadius: 10,
                   padding: "12px 16px", fontSize: 15, fontWeight: "bold", color: "#e8e4dc",
                   lineHeight: 1.4, overflowWrap: "break-word" },
  pillCheckAmber:{ color: "#d97706", fontWeight: "bold", whiteSpace: "nowrap" },
  pillCheckTeal: { color: "#2dd4bf", fontWeight: "bold", whiteSpace: "nowrap" },
  pillUnverified:{ color: "#7a7570", fontWeight: "normal", fontSize: 13 },
  pillSub:       { fontSize: 12, color: "#7a7570", marginTop: 6 },

  goalCard:      { background: "#111316", border: "1px solid #1e2022", borderRadius: 12,
                   padding: "22px 26px", marginBottom: 16 },
  goalLabel:     { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
                   color: "#6a6660", fontWeight: "bold", marginBottom: 4 },
  goalName:      { fontSize: 19, fontWeight: "bold", color: "#e8e4dc", marginBottom: 16,
                   letterSpacing: "-0.3px", overflowWrap: "break-word" },
  goalBarText:   { color: "#9a9490", fontSize: 13, marginTop: 8 },
  checklist:     { display: "flex", flexDirection: "column", gap: 7, marginTop: 16 },
  checkItem:     { display: "flex", alignItems: "baseline", gap: 8, fontSize: 13,
                   flexWrap: "wrap", lineHeight: 1.4 },
  linkToggle:    { background: "none", border: "none", color: "#d97706", cursor: "pointer",
                   fontFamily: "inherit", fontSize: 13, fontWeight: "bold", padding: 0,
                   marginTop: 18 },
  otherRole:     { borderTop: "1px solid #1e2022", paddingTop: 14, marginTop: 14 },
  otherRoleName: { fontSize: 14, fontWeight: "bold", color: "#d97706", marginBottom: 2 },

  roadmapCard:   { background: "#111316", border: "1px solid #1e2022", borderRadius: 12,
                   marginBottom: 16, overflow: "hidden" },
  roadmapHeader: { display: "flex", alignItems: "center", justifyContent: "space-between",
                   width: "100%", background: "none", border: "none", cursor: "pointer",
                   fontFamily: "inherit", padding: "16px 20px", color: "#e8e4dc",
                   textAlign: "left" },
  roadmapTitle:  { fontSize: 14, fontWeight: "bold", letterSpacing: "0.02em" },
  roadmapToggle: { fontSize: 13, color: "#d97706", fontWeight: "bold", whiteSpace: "nowrap" },
  roadmapSummary:{ padding: "0 20px 16px", fontSize: 13, color: "#7a7570" },
  roadmapRows:   { borderTop: "1px solid #1e2022" },

  // Empty states
  emptyCard:     { maxWidth: 480, margin: "80px auto", background: "#111316",
                   border: "1px solid #1e2022", borderRadius: 12,
                   padding: "48px 40px", textAlign: "center" },
  emptyTitle:    { fontSize: 24, fontWeight: "bold", margin: "0 0 12px" },
  emptyMsg:      { color: "#7a7570", lineHeight: 1.6, margin: "0 0 24px" },
  emptyState:    { background: "#111316", border: "1px solid #1e2022",
                   borderRadius: 10, padding: "40px", textAlign: "center",
                   color: "#7a7570" },
  btnPrimary:    { background: "#d97706", border: "none", color: "#0d0f10",
                   padding: "11px 22px", fontSize: 14, fontWeight: "bold",
                   borderRadius: 6, cursor: "pointer", fontFamily: "inherit" },
};
